import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Language = 'zh' | 'en';

interface LanguageState {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
}

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      language: 'zh',
      setLanguage: (lang: Language) => set({ language: lang }),
      toggleLanguage: () => set({ language: get().language === 'zh' ? 'en' : 'zh' }),
    }),
    {
      name: 'taohuatan-language-storage',
    }
  )
);

export const t = (key: string): string => {
  const { language } = useLanguageStore.getState();
  return translations[language][key] || key;
};

const translations: Record<Language, Record<string, string>> = {
  zh: {
    // Common
    'common.loading': '加载中...',
    'common.submit': '提交',
    'common.cancel': '取消',
    'common.confirm': '确认',
    'common.delete': '删除',
    'common.edit': '编辑',
    'common.save': '保存',
    'common.back': '返回',
    'common.next': '下一步',
    'common.previous': '上一步',
    'common.search': '搜索',
    'common.filter': '筛选',
    'common.reset': '重置',
    'common.export': '导出',
    'common.import': '导入',
    'common.refresh': '刷新',
    'common.close': '关闭',
    'common.yes': '是',
    'common.no': '否',
    'common.success': '成功',
    'common.error': '错误',
    'common.warning': '警告',
    'common.info': '提示',
    'common.noData': '暂无数据',
    'common.optional': '可选',
    'common.required': '必填',
    'common.actions': '操作',
    'common.status': '状态',
    'common.createdAt': '创建时间',
    'common.updatedAt': '更新时间',
    'common.total': '总计',
    'common.page': '页',
    'common.perPage': '条/页',
    'common.all': '全部',
    'common.more': '更多',
    'common.view': '查看',
    'common.download': '下载',
    'common.upload': '上传',
    'common.copy': '复制',
    'common.copied': '已复制',
    'common.selectAll': '全选',
    'common.deselectAll': '取消全选',
    'common.pleaseSelect': '请选择',
    'common.pleaseEnter': '请输入',
    'common.confirmDelete': '确认删除？',
    'common.deleteSuccess': '删除成功',
    'common.saveSuccess': '保存成功',
    'common.operationFailed': '操作失败',
    'common.networkError': '网络错误',
    'common.searchPlaceholder': '搜索帖子...',

    // Navigation
    'nav.home': '首页',
    'nav.posts': '帖子',
    'nav.rooms': '会员房间',
    'nav.storage': '云存储',
    'nav.messages': '消息',
    'nav.userCenter': '个人中心',
    'nav.login': '登录',
    'nav.register': '注册',
    'nav.logout': '退出登录',
    'nav.admin': '后台管理',
    'nav.master': '站长后台',
    'nav.about': '关于我们',
    'nav.contact': '联系我们',
    'nav.faq': '帮助中心',
    'nav.privacy': '隐私政策',
    'nav.terms': '服务条款',
    'nav.donation': '赞助我们',
    'nav.joinUs': '加入我们',
    'nav.chat': '即时闲聊',
    'nav.createPost': '发帖',
    'nav.categories': '版块',
    'nav.roomManagement': '我的房间管理',

    // User Center
    'userCenter.title': '个人中心',
    'userCenter.profile': '个人资料',
    'userCenter.changePassword': '修改密码',
    'userCenter.nickname': '昵称',
    'userCenter.signature': '个性签名',
    'userCenter.avatar': '头像',
    'userCenter.changeAvatar': '更换头像',
    'userCenter.oldPassword': '原密码',
    'userCenter.newPassword': '新密码',
    'userCenter.confirmPassword': '确认密码',
    'userCenter.passwordMismatch': '两次密码不一致',
    'userCenter.updateSuccess': '更新成功',
    'userCenter.stats': '数据统计',
    'userCenter.level': '等级信息',
    'userCenter.growth': '成长记录',
    'userCenter.points': '积分明细',
    'userCenter.myPosts': '我的帖子',
    'userCenter.myReplies': '我的回复',
    'userCenter.achievements': '成就',
    'userCenter.currentLevel': '当前等级',
    'userCenter.nextLevel': '下一等级',
    'userCenter.experience': '经验值',
    'userCenter.pointsBalance': '积分余额',
    'userCenter.postCount': '发帖数',
    'userCenter.replyCount': '回帖数',
    'userCenter.likesReceived': '获赞数',
    'userCenter.viewsReceived': '被浏览数',

    // Home
    'home.welcome': '欢迎来到桃花坛',
    'home.latestPosts': '最新帖子',
    'home.hotPosts': '热门帖子',
    'home.categories': '分类',
    'home.viewAll': '查看全部',
    'home.postsCount': '帖子数',
    'home.repliesCount': '回复数',
    'home.lastActivity': '最后活动',
    'home.noPosts': '暂无帖子',
    'home.lastReply': '最后回复',
    'home.previousPage': '上一页',
    'home.nextPage': '下一页',
    'home.pageInfo': '第 {page} / {totalPages} 页',

    // Category
    'category.title': '版块',
    'category.postsCount': '帖子数',
    'category.noPosts': '该版块暂无帖子',

    // Posts
    'posts.title': '帖子',
    'posts.create': '发布帖子',
    'posts.edit': '编辑帖子',
    'posts.delete': '删除帖子',
    'posts.content': '内容',
    'posts.category': '分类',
    'posts.author': '作者',
    'posts.createdAt': '发布时间',
    'posts.updatedAt': '更新时间',
    'posts.views': '浏览',
    'posts.likes': '点赞',
    'posts.replies': '回复',
    'posts.like': '点赞',
    'posts.reply': '回复',
    'posts.share': '分享',
    'posts.report': '举报',
    'posts.top': '置顶',
    'posts.globalTop': '全站置顶',
    'posts.categoryTop': '板块置顶',
    'posts.essence': '精华',
    'posts.locked': '锁定',
    'posts.pending': '待审核',
    'posts.approved': '已通过',
    'posts.rejected': '已拒绝',
    'posts.publish': '发布',
    'posts.draft': '草稿',
    'posts.preview': '预览',
    'posts.titlePlaceholder': '请输入帖子标题',
    'posts.contentPlaceholder': '请输入帖子内容，支持 Markdown 格式',
    'posts.selectCategory': '请选择分类',
    'posts.minLength': '最少10个字符',
    'posts.maxLength': '最多50000个字符',
    'posts.publishSuccess': '发布成功',
    'posts.updateSuccess': '更新成功',
    'posts.deleteSuccess': '删除成功',
    'posts.deleteConfirm': '确定要删除这篇帖子吗？',
    'posts.notFound': '帖子不存在',
    'posts.noPermission': '没有权限',
    'posts.replyPlaceholder': '写下你的回复...',
    'posts.uploadImage': '上传图片',
    'posts.uploadAttachment': '上传附件',
    'posts.imageTooLarge': '图片大小不能超过5MB',
    'posts.unsupportedFormat': '不支持的图片格式',
    'posts.canDelete': '可以删除自己的回复',
    'posts.adminCanDelete': '管理员可以删除任何回复',

    // Chat
    'chat.title': '聊天室',
    'chat.send': '发送',
    'chat.placeholder': '输入消息...',
    'chat.image': '图片',
    'chat.file': '文件',
    'chat.recall': '撤回',
    'chat.delete': '删除',
    'chat.more': '加载更多',
    'chat.noMessages': '暂无消息',
    'chat.sendSuccess': '发送成功',
    'chat.sendFailed': '发送失败',
    'chat.uploadFailed': '上传失败',
    'chat.chatRoom': '即时闲聊',
    'chat.chatNow': '快来发言吧！',
    'chat.imageMessage': '图片消息',
    'chat.preview': '预览',
    'chat.pleaseLogin': '请登录后发送消息',
    'chat.sending': '发送中...',

    // Room
    'room.title': '会员房间',
    'room.join': '加入房间',
    'room.leave': '离开房间',
    'room.create': '创建房间',
    'room.manage': '管理房间',
    'room.owner': '房主',
    'room.members': '成员',
    'room.memberCount': '成员数',
    'room.maxMembers': '最大成员数',
    'room.description': '房间描述',
    'room.settings': '房间设置',
    'room.qrCode': '收款码',
    'room.price': '价格',
    'room.period': '周期',
    'room.monthly': '月付',
    'room.quarterly': '季付',
    'room.yearly': '年付',
    'room.permanent': '永久',
    'room.joinRoom': '加入房间',
    'room.exitRoom': '退出房间',
    'room.roomFull': '房间已满',
    'room.notOwner': '不是房主',
    'room.alreadyJoined': '已加入该房间',
    'room.notJoined': '未加入该房间',
    'room.pause': '暂停',
    'room.resume': '恢复',
    'room.paused': '已暂停',
    'room.active': '进行中',
    'room.hidden': '已隐藏',
    'room.public': '公开',
    'room.private': '私密',
    'room.description_': '加入付费房间，与志同道合的朋友交流。联系站长开通房主权限',

    // Membership
    'membership.title': '会员套餐',
    'membership.purchase': '购买套餐',
    'membership.upgrade': '升级套餐',
    'membership.renew': '续费套餐',
    'membership.expired': '已过期',
    'membership.current': '当前套餐',
    'membership.expiresAt': '到期时间',
    'membership.daysLeft': '剩余天数',
    'membership.vip': 'VIP会员',
    'membership.svip': 'SVIP会员',
    'membership.unlimited': '无限期',

    // Payment
    'payment.title': '支付',
    'payment.method': '支付方式',
    'payment.address': '收款地址',
    'payment.amount': '支付金额',
    'payment.orderNo': '订单号',
    'payment.status': '订单状态',
    'payment.pending': '待支付',
    'payment.paid': '已支付',
    'payment.failed': '支付失败',
    'payment.cancelled': '已取消',
    'payment.expired': '已过期',
    'payment.confirm': '确认支付',
    'payment.confirmManual': '手动确认',
    'payment.waiting': '等待支付',
    'payment.processing': '处理中',
    'payment.success': '支付成功',
    'payment.failed_': '支付失败',
    'payment.timeout': '支付超时',
    'payment.retry': '重试',
    'payment.copyAddress': '复制地址',
    'payment.scanQrCode': '扫码支付',
    'payment.manualConfirmTip': '请向指定地址转账后点击确认',

    // Storage
    'storage.title': '云存储',
    'storage.used': '已用空间',
    'storage.total': '总空间',
    'storage.files': '我的文件',
    'storage.purchase': '购买空间',
    'storage.upgrade': '扩容',
    'storage.delete': '删除',
    'storage.download': '下载',
    'storage.rename': '重命名',
    'storage.share': '分享',
    'storage.noFiles': '暂无文件',
    'storage.fileName': '文件名',
    'storage.fileSize': '文件大小',
    'storage.uploadTime': '上传时间',
    'storage.selectFile': '选择文件',
    'storage.dragFile': '拖拽文件到此处',
    'storage.orClick': '或点击上传',

    // Admin
    'admin.title': '后台管理',
    'admin.dashboard': '控制台',
    'admin.users': '用户管理',
    'admin.posts': '帖子管理',
    'admin.categories': '分类管理',
    'admin.rooms': '房间管理',
    'admin.orders': '订单管理',
    'admin.ads': '广告管理',
    'admin.config': '网站配置',
    'admin.logs': '操作日志',
    'admin.chat': '聊天室管理',
    'admin.sensitiveWords': '敏感词管理',
    'admin.userGroups': '用户组管理',
    'admin.packages': '套餐管理',
    'admin.reconciliation': '对账管理',
    'admin.pendingOrders': '待处理订单',
    'admin.completedOrders': '已完成订单',
    'admin.allOrders': '全部订单',
    'admin.roomOrders': '房间订单',
    'admin.membershipOrders': '会员订单',
    'admin.storageOrders': '存储订单',
    'admin.donationOrders': '赞助订单',
    'admin.expansionOrders': '扩容订单',
    'admin.roomMembershipOrders': '房间会员订单',
    'admin.roomCreationOrders': '房主创建订单',
    'admin.createRoom': '创建房间',
    'admin.pauseRoom': '暂停房间',
    'admin.resumeRoom': '恢复房间',
    'admin.deleteRoom': '删除房间',
    'admin.viewRoom': '查看房间',
    'admin.editRoom': '编辑房间',
    'admin.approve': '通过',
    'admin.reject': '拒绝',
    'admin.batchApprove': '批量通过',
    'admin.batchReject': '批量拒绝',
    'admin.searchUser': '搜索用户',
    'admin.searchPost': '搜索帖子',
    'admin.searchOrder': '搜索订单',
    'admin.totalUsers': '总用户数',
    'admin.totalPosts': '总帖子数',
    'admin.totalOrders': '总订单数',
    'admin.todayOrders': '今日订单',
    'admin.todayRevenue': '今日收入',
    'admin.activeUsers': '活跃用户',
    'admin.newUsers': '新增用户',
    'admin.userId': '用户ID',
    'admin.username': '用户名',
    'admin.email': '邮箱',
    'admin.role': '角色',
    'admin.banned': '禁用',
    'admin.active': '正常',
    'admin.ban': '封禁',
    'admin.unban': '解封',
    'admin.banReason': '封禁原因',
    'admin.banSuccess': '封禁成功',
    'admin.unbanSuccess': '解封成功',
    'admin.batchOperation': '批量操作',
    'admin.selectUsers': '选择用户',
    'admin.selectPosts': '选择帖子',
    'admin.selectOrders': '选择订单',
    'admin.confirmBatch': '确认批量操作',
    'admin.batchSuccess': '批量操作成功',
    'admin.word': '敏感词',
    'admin.wordType': '词类型',
    'admin.replaceWith': '替换为',
    'admin.addWord': '添加敏感词',
    'admin.importWords': '批量导入',
    'admin.exportWords': '导出',
    'admin.groupName': '组名称',
    'admin.groupDescription': '组描述',
    'admin.permissions': '权限',
    'admin.addGroup': '添加用户组',
    'admin.editGroup': '编辑用户组',
    'admin.deleteGroup': '删除用户组',
    'admin.setGroup': '设置用户组',
    'admin.updateGroup': '更新用户组',
    'admin.cannotUpdateSelf': '不能修改自己的用户组',

    // Admin Dashboard
    'admin.quickActions': '快捷操作',
    'admin.manageUsers': '管理用户账号',
    'admin.managePosts': '管理帖子内容',
    'admin.manageCategories': '管理论坛版块',
    'admin.manageSensitiveWords': '管理敏感词列表',
    'admin.configureSite': '配置站点设置',
    'admin.totalReplies': '回复总数',
    'admin.totalCategories': '版块总数',

    // Admin Users
    'admin.searchUserId': '搜索用户ID、用户名、昵称、邮箱...',
    'admin.searching': '搜索中...',
    'admin.foundResults': '找到 {count} 个结果',
    'admin.noMatchingUsers': '未找到匹配的用户',
    'admin.noUsers': '暂无用户数据',
    'admin.noUserGroup': '无用户组',
    'admin.registerTime': '注册时间',

    // Admin Posts
    'admin.postTitle': '标题',
    'admin.movePost': '移动帖子到其他版块',
    'admin.globalTop': '全站置顶',
    'admin.categoryTop': '板块置顶',
    'admin.untop': '取消置顶',
    'admin.globalTopSuccess': '全站置顶成功',
    'admin.categoryTopSuccess': '板块置顶成功',
    'admin.untopSuccess': '取消置顶成功',
    'admin.essence': '精华',
    'admin.cancelEssence': '取消精华',
    'admin.setEssenceSuccess': '设为精华成功',
    'admin.cancelEssenceSuccess': '取消精华成功',
    'admin.confirmDeletePost': '确定要删除该帖子吗？此操作不可撤销。',
    'admin.currentCategory': '当前版块',
    'admin.selectTargetCategory': '选择目标版块',
    'admin.moveSuccess': '移动帖子成功',
    'admin.moveFailed': '移动失败',
    'admin.views': '浏览',
    'admin.replies': '回复',
    'admin.likes': '点赞',
    'admin.normal': '正常',
    'admin.deleted': '已删除',

    // Admin Categories
    'admin.categoryName': '版块名称',
    'admin.categoryDescription': '版块描述',
    'admin.createCategory': '创建版块',
    'admin.asTopLevelCategory': '作为顶级版块',
    'admin.asChildCategory': '作为 {name} 的子版块',
    'admin.description': '描述',
    'admin.postCount': '帖子数',
    'admin.show': '显示',
    'admin.hide': '隐藏',
    'admin.confirmDeleteCategory': '确定要删除该版块吗？此操作不可撤销。',

    // Room Management
    'admin.roomManagement': '房间后台管理',
    'admin.selectRoom': '选择房间',
    'admin.searchOwner': '搜索房主...',
    'admin.noRooms': '暂无房间',
    'admin.noRoomsCreated': '您还没有创建房间',
    'admin.createNewRoom': '创建新房间',
    'admin.roomSettings': '房间设置',
    'admin.banManagement': '禁言管理',
    'admin.apiKeys': 'API密钥',
    'admin.aiConfig': 'AI配置',
    'admin.memberManagement': '成员管理',
    'admin.roomName': '房间名称',
    'admin.priceUsdt': '价格 (USDT)',
    'admin.pricingModel': '收费模式',
    'admin.monthly': '月付',
    'admin.yearly': '年付',
    'admin.oneTime': '一次性',
    'admin.roomDescription': '房间描述',
    'admin.trc20Address': 'TRC20 收款地址',
    'admin.erc20Address': 'ERC20 收款地址',
    'admin.maxMembers': '最大成员数',
    'admin.paymentQrCode': '收款二维码',
    'admin.upload': '上传',
    'admin.jpgPngMax5mb': 'JPG/PNG，最大 5MB',
    'admin.enableRoom': '启用房间',
    'admin.hideRoom': '隐藏房间',
    'admin.roomValidityPeriod': '房间有效期',
    'admin.expireTime': '到期时间',
    'admin.roomExpired': '房间已过期',
    'admin.renew': '续费',
    'admin.banList': '禁言列表',
    'admin.addBan': '添加禁言',
    'admin.noBanUsers': '暂无禁言用户',
    'admin.userId': '用户ID',
    'admin.reason': '原因',
    'admin.expire': '过期',
    'admin.permanentBan': '永久禁言',
    'admin.removeBan': '解除禁言',
    'admin.aiFeatureConfig': 'AI 功能配置',
    'admin.aiAssistant': 'AI 助手功能',
    'admin.aiAssistantDesc': '配置房间内的 AI 助手功能，为会员提供智能问答服务。AI 功能可以帮助会员解答问题，提高房间活跃度。',
    'admin.enableAiAssistant': '启用 AI 助手',
    'admin.enableAiAssistantDesc': '开启后，会员可以在房间内使用 AI 问答功能',
    'admin.aiResponseRateLimit': 'AI 响应频率限制',
    'admin.aiRateLow': '低（每用户每小时10次）',
    'admin.aiRateMedium': '中（每用户每小时30次）',
    'admin.aiRateHigh': '高（每用户每小时60次）',
    'admin.aiRateUnlimited': '不限制',
    'admin.aiModelSelection': 'AI 模型选择',
    'admin.gpt35': 'GPT-3.5（快速响应）',
    'admin.gpt4': 'GPT-4（更高质量）',
    'admin.claude': 'Claude（平衡型）',
    'admin.systemPrompt': '系统提示词',
    'admin.systemPromptPlaceholder': '定义 AI 助手的角色和行为规范...',
    'admin.manualEnableMember': '手动开通会员',
    'admin.currentMembers': '当前成员',
    'admin.enabledMembers': '已开通会员',
    'admin.searchMembers': '搜索成员...',
    'admin.noMembers': '暂无成员',
    'admin.enabledMemberList': '已开通会员列表',
    'admin.refresh': '刷新',
    'admin.noEnabledMembers': '暂无已开通会员记录',
    'admin.notes': '注意事项',
    'admin.manualEnableNote': '手动开通功能用于特殊情况，例如：用户通过链上转账但未自动到账、优惠活动、赠送会员等。开通记录将被日志记录。',
    'admin.apiKeyManagement': 'API密钥管理',
    'admin.createKey': '创建密钥',
    'admin.newApiKey': '新创建的API密钥',
    'admin.saveKeyNote': '请妥善保存密钥，关闭后将无法再次查看完整密钥',
    'admin.noApiKeys': '暂无API密钥',
    'admin.enable': '启用',
    'admin.disable': '禁用',
    'admin.resetKey': '重置密钥',
    'admin.externalApiDoc': '外部API调用说明',
    'admin.requestUrl': '请求地址',
    'admin.headers': 'Headers',
    'admin.body': 'Body (JSON)',
    'admin.roomId': '房间ID',
    'admin.messageContent': '消息内容',
    'admin.welcomeRoomManagement': '欢迎使用房间管理后台',
    'admin.noRoomsPleaseCreate': '暂无房间，请先创建房间',
    'admin.goCreateRoom': '去创建房间',
    'admin.enterRoomName': '输入房间名称',
    'admin.enterRoomDescription': '输入房间描述',
    'admin.enterTrc20Address': '输入您的 TRC20 钱包地址',
    'admin.trc20Network': 'TRC20 (TRON 网络)',
    'admin.enterErc20Address': '输入您的 ERC20 钱包地址',
    'admin.erc20Network': 'ERC20 (Ethereum 网络)',
    'admin.noLimit': '不限制',
    'admin.maxMemberLimit': '房间最大成员数量限制',
    'admin.uploadQrCodeImage': '上传二维码图片',
    'admin.optionalQrCode': '可选：提供二维码图片方便用户扫码支付',
    'admin.cancel': '取消',
    'admin.createRoom': '创建房间',
    'admin.banUser': '禁言',
    'admin.enterUserId': '输入用户ID',
    'admin.banReason': '禁言原因',
    'admin.enterBanReason': '输入禁言原因',
    'admin.banDuration': '禁言时长（小时）',
    'admin.permanentBanNote': '留空表示永久禁言',
    'admin.keyName': '密钥名称',
    'admin.enterKeyName': '输入密钥名称（可选）',
    'admin.usernameOrEmail': '用户名/邮箱',
    'admin.enterUsernameOrEmail': '输入用户名或邮箱搜索用户',
    'admin.memberType': '会员类型',
    'admin.normalMember': '普通会员',
    'admin.roomAdmin': '房间管理员',
    'admin.expireTime2': '过期时间',
    'admin.permanentMember': '永久会员',
    'admin.confirmEnable': '确认开通',
    'admin.renewRoom': '续费房间',
    'admin.selectRenewPeriod': '选择续费时长',
    'admin.quarterly': '季付',
    'admin.confirmRenew': '确认续费',

    // Pending Orders
    'admin.pendingOrders': '待处理订单',
    'admin.searchUsername': '搜索用户名...',
    'admin.type': '类型',
    'admin.amount': '金额',
    'admin.txid': 'TXID',
    'admin.remainingTime': '剩余时间',
    'admin.createTime': '创建时间',
    'admin.operation': '操作',
    'admin.completeOrder': '完成订单',
    'admin.refundOrder': '退款订单',
    'admin.selectPackageUpgrade': '选择套餐扩容',
    'admin.orderId': '订单ID',
    'admin.user': '用户',
    'admin.unknown': '未知',
    'admin.enterActualSpace': '原订单未付款，请输入实际空间大小',
    'admin.selectPackage': '选择套餐',
    'admin.confirmOperation': '确认操作',
    'admin.timedOut': '已超时',
    'admin.noPendingOrders': '暂无待处理订单',
    'admin.totalRecords': '共 {total} 条记录，第 {page} / {totalPages} 页',
    'admin.previousPage': '上一页',
    'admin.nextPage': '下一页',
    'admin.orderCompleted': '已完成',
    'admin.cancelOrder': '取消订单',
    'admin.selectPackageToExpand': '选择套餐扩容',

    // Admin Master
    'admin.dashboard': '控制台',
    'admin.users': '用户管理',
    'admin.categories': '版块管理',
    'admin.posts': '帖子管理',
    'admin.sensitiveWordLibrary': '敏感词库',
    'admin.chatManagement': '聊天室管理',
    'admin.vipRoomManagement': '会员房间管理',
    'admin.orderManagement': '订单管理',
    'admin.expansionOrders': '扩容订单',
    'admin.donationOrders': '捐助订单',
    'admin.roomOrders': '房间订单管理',
    'admin.experience': '心得管理',
    'admin.siteConfig': '站点配置',
    'admin.adManagement': '广告管理',
    'admin.stats': '数据统计',
    'admin.operationLogs': '操作日志',
    'admin.loadDataFailed': '加载数据失败',
    'admin.loadUserListFailed': '加载用户列表失败',
    'admin.loadUserGroupListFailed': '加载用户组列表失败',
    'admin.updateUserGroupSuccess': '更新用户组成功',
    'admin.createUserGroupSuccess': '创建用户组成功',
    'admin.saveUserGroupFailed': '保存用户组失败',
    'admin.deleteUserGroupSuccess': '删除用户组成功',
    'admin.deleteUserGroupFailed': '删除用户组失败',

    // Auth
    'auth.login': '登录',
    'auth.register': '注册',
    'auth.logout': '退出',
    'auth.username': '用户名',
    'auth.email': '邮箱',
    'auth.password': '密码',
    'auth.confirmPassword': '确认密码',
    'auth.forgotPassword': '忘记密码？',
    'auth.noAccount': '还没有账号？',
    'auth.hasAccount': '已有账号？',
    'auth.registerNow': '立即注册',
    'auth.loginNow': '立即登录',
    'auth.rememberMe': '记住我',
    'auth.verificationCode': '验证码',
    'auth.sendCode': '发送验证码',
    'auth.codeSent': '验证码已发送',
    'auth.resetPassword': '重置密码',
    'auth.resetSuccess': '密码重置成功',
    'auth.resetFailed': '密码重置失败',
    'auth.invalidCode': '验证码错误',
    'auth.codeExpired': '验证码已过期',
    'auth.loginSuccess': '登录成功',
    'auth.registerSuccess': '注册成功',
    'auth.logoutSuccess': '退出成功',
    'auth.invalidCredentials': '用户名或密码错误',
    'auth.emailExists': '邮箱已被注册',
    'auth.usernameExists': '用户名已被占用',
    'auth.weakPassword': '密码强度太弱',
    'auth.passwordTooShort': '密码至少6位',
    'auth.emailInvalid': '邮箱格式不正确',
    'auth.usernameInvalid': '用户名格式不正确',
    'auth.usernameLength': '用户名至少3个字符',
    'auth.pleaseLogin': '请先登录',
    'auth.pleaseVerifyEmail': '请先验证邮箱',
    'auth.emailVerified': '邮箱已验证',
    'auth.emailNotVerified': '邮箱未验证',
    'auth.verifyEmail': '验证邮箱',
    'auth.verifyEmailSuccess': '邮箱验证成功',
    'auth.verifyEmailFailed': '邮箱验证失败',
    'auth.resendVerification': '重新发送验证邮件',
    'auth.verificationEmailSent': '验证邮件已发送',
    'auth.verificationEmailSentTip': '请查收邮件并点击验证链接',
    'auth.clickToVerify': '点击这里发送验证邮件',
    'auth.usernameOrEmail': '用户名或邮箱',
    'auth.enterUsernameOrEmail': '请输入用户名或邮箱',
    'auth.enterPassword': '请输入密码',
    'auth.loggingIn': '登录中...',
    'auth.loginFailed': '登录失败',
    'auth.noAccountYet': '还没有账号？',

    // Register
    'register.title': '注册',
    'register.username': '用户名',
    'register.email': '邮箱',
    'register.password': '密码',
    'register.confirmPassword': '确认密码',
    'register.enterUsername': '请输入用户名（3-20个字符）',
    'register.enterEmail': '请输入邮箱地址',
    'register.enterPassword': '请输入密码（至少6位）',
    'register.confirmPasswordPlaceholder': '请再次输入密码',
    'register.registering': '注册中...',
    'register.hasAccount': '已有账号？',
    'register.loginNow': '立即登录',
    'register.fillRequired': '请填写所有必填项',
    'register.passwordMismatch': '两次输入的密码不一致',
    'register.passwordTooShort': '密码长度至少6位',
    'register.success': '注册成功，请查收邮件验证邮箱',
    'register.failed': '注册失败',
    'register.verificationEmailSent': '验证邮件已发送',
    'register.sentVerificationEmail': '我们已向 {email} 发送了一封验证邮件',
    'register.checkEmail': '请查收邮件并点击验证链接完成注册',
    'register.willRedirect': '页面将在 {seconds} 秒后自动跳转到登录页面...',
    'register.backToLogin': '返回登录',
    'register.sending': '发送中...',
    'register.resendVerification': '重新发送验证邮件',
    'register.resendSuccess': '验证邮件已重新发送，请查收',
    'register.sendFailed': '发送失败，请稍后再试',
    'register.usernameTip': '3-20个字符，字母、数字、下划线',
    'register.passwordTip': '至少6位字符',

    // Posts
    'posts.title': '帖子',
    'posts.detail': '帖子详情',
    'posts.floor': '楼层',
    'posts.floorDetail': '楼中楼',
    'posts.op': '楼主',
    'posts.downloadCount': '次下载',
    'posts.download': '下载',
    'posts.edit': '编辑',
    'posts.delete': '删除',
    'posts.replyComment': '回复评论',
    'posts.postReply': '发表回复',
    'posts.replyingTo': '正在回复',
    'posts.cancel': '取消',
    'posts.uploading': '上传中...',
    'posts.uploadImage': '上传图片',
    'posts.uploadAttachment': '上传附件',
    'posts.attachments': '附件',
    'posts.attachmentsAdded': '已添加附件',
    'posts.writeReply': '写下你的回复...',
    'posts.sending': '发送中...',
    'posts.sendReply': '发送回复',
    'posts.loginToJoin': '登录后可参与讨论',
    'posts.noReplies': '暂无回复',
    'posts.login': '登录',
    'posts.confirmDelete': '确认删除',
    'posts.confirmDeletePost': '确定要删除这篇帖子吗？此操作不可撤销。',
    'posts.irrevocable': '此操作不可撤销',
    'posts.replySuccess': '回复成功',
    'posts.replyFailed': '回复失败',
    'posts.attachmentTooLarge': '附件大小不能超过10MB',
    'posts.attachmentUploadSuccess': '附件上传成功',
    'posts.attachmentUploadFailed': '附件上传失败',
    'posts.imageUploadSuccess': '图片上传成功',
    'posts.uploadFailed': '上传失败',
    'posts.deleteSuccess': '删除成功',
    'posts.deleteFailed': '删除失败',
    'posts.confirmDeleteReply': '确定要删除这条回复吗？',
    'posts.notFound': '帖子不存在',

    // Create Post
    'createPost.title': '发布新帖',
    'createPost.selectBoard': '选择版块',
    'createPost.pleaseSelectBoard': '请选择版块',
    'createPost.board': '版块',
    'createPost.postTitle': '标题',
    'createPost.pleaseEnterTitle': '请输入标题（5-100个字符）',
    'createPost.titleLength': '（5-100个字符）',
    'createPost.content': '内容',
    'createPost.pleaseEnterContent': '支持 Markdown 格式，请输入帖子内容（10-50000个字符）',
    'createPost.contentLength': '（10-50000个字符）',
    'createPost.markdownTip': '支持 Markdown 语法，可以使用 **粗体**、*斜体*、`代码`、[链接](url) 等',
    'createPost.attachedFiles': '已上传附件',
    'createPost.cancel': '取消',
    'createPost.publishing': '发布中...',
    'createPost.publishPost': '发布帖子',
    'createPost.imageUploadSuccess': '图片上传成功',
    'createPost.uploadFailed': '上传失败',
    'createPost.uploading': '上传中...',
    'createPost.uploadImage': '上传图片',
    'createPost.uploadAttachment': '上传附件',
    'createPost.fileTooLarge': '文件大小不能超过100MB',
    'createPost.attachmentUploadSuccess': '附件上传成功',
    'createPost.enterTitle': '请输入标题',
    'createPost.enterContent': '请输入内容',
    'createPost.selectCategory': '请选择版块',
    'createPost.postSuccess': '发帖成功',
    'createPost.postFailed': '发帖失败',

    // Errors
    'error.network': '网络错误，请稍后重试',
    'error.server': '服务器错误，请稍后重试',
    'error.notFound': '页面不存在',
    'error.unauthorized': '未授权，请登录',
    'error.forbidden': '没有权限访问',
    'error.badRequest': '请求参数错误',
    'error.tooManyRequests': '请求过于频繁，请稍后重试',
    'error.unknown': '未知错误',

    // Footer
    'footer.copyright': '© 2024 桃花坛 版权所有',
    'footer.madeWith': '用 ❤️ 构建',

    // Messages
    'messages.title': '消息中心',
    'messages.system': '系统消息',
    'messages.comments': '评论消息',
    'messages.likes': '点赞消息',
    'messages.mentions': '@提及消息',
    'messages.at': '有人在',
    'messages.mentioned': '中提到了你',
    'messages.liked': '点赞了你的',
    'messages.commented': '评论了你的',
    'messages.reply': '回复',
    'messages.post': '帖子',
    'messages.read': '已读',
    'messages.unread': '未读',
    'messages.markAllRead': '全部已读',
    'messages.noMessages': '暂无消息',
    'messages.clear': '清空消息',

    // About
    'about.title': '关于我们',
    'about.description': '桃花坛是一个致力于技术分享与交流的社区平台',
    'about.contact': '联系我们',
    'about.email': '邮箱',
    'about.phone': '电话',
    'about.address': '地址',

    // Other
    'other.loading': '加载中...',
    'other.noMore': '没有更多了',
    'other.loadingMore': '加载更多...',
    'other.pullToRefresh': '下拉刷新',
    'other.releaseToRefresh': '释放刷新',
    'other.refreshing': '刷新中...',

    // User Profile
    'userProfile.title': '用户资料',
    'userProfile.level': '等级',
    'userProfile.points': '积分',
    'userProfile.posts': '帖子',
    'userProfile.replies': '回复',
    'userProfile.registeredAt': '注册于',
    'userProfile.publishedPosts': '发布的帖子',
    'userProfile.noPosts': '暂无帖子',

    // Storage
    'storage.loadFailed': '加载数据失败',
    'storage.selectFile': '请先选择文件',
    'storage.fileTooLarge': '文件大小不能超过100MB',
    'storage.uploadSuccess': '上传成功！',
    'storage.uploadFailed': '上传失败',
    'storage.deleteSuccess': '删除成功',
    'storage.deleteFailed': '删除失败',
    'storage.confirmPayment': '确定要确认支付这个订单吗？',
    'storage.paymentSuccess': '支付成功！获得',
    'storage.confirmPaymentFailed': '确认支付失败',

    // Edit Post
    'editPost.title': '编辑帖子',
    'editPost.backToPost': '返回帖子',
    'editPost.postTitle': '标题',
    'editPost.enterTitle': '请输入帖子标题',
    'editPost.category': '版块',
    'editPost.selectCategory': '请选择版块',
    'editPost.content': '内容',
    'editPost.enterContent': '请输入帖子内容',
    'editPost.submitting': '提交中...',
    'editPost.saveChanges': '保存修改',
    'editPost.noPermission': '没有权限编辑此帖子',
    'editPost.fetchFailed': '获取数据失败',
    'editPost.fillAll': '请填写完整的帖子信息',
    'editPost.editFailed': '编辑失败',
    'editPost.notFound': '帖子不存在',

    // Search Results
    'searchResults.title': '搜索结果',
    'searchResults.keyword': '关键词',
    'searchResults.resultsCount': '共 {total} 条结果',
    'searchResults.previousPage': '上一页',
    'searchResults.nextPage': '下一页',
    'searchResults.pageInfo': '第 {page} / {totalPages} 页',
    'searchResults.noResults': '没有找到相关帖子',

    // Forgot Password
    'forgotPassword.title': '忘记密码',
    'forgotPassword.enterEmail': '输入您的注册邮箱，我们会发送密码重置链接',
    'forgotPassword.emailAddress': '邮箱地址',
    'forgotPassword.enterRegisteredEmail': '请输入注册邮箱',
    'forgotPassword.sending': '发送中...',
    'forgotPassword.sendResetLink': '发送重置链接',
    'forgotPassword.rememberPassword': '想起密码了？',
    'forgotPassword.backToLogin': '返回登录',
    'forgotPassword.enterEmailAddress': '请输入邮箱地址',
    'forgotPassword.resetEmailSent': '重置密码邮件已发送，请查收',
    'forgotPassword.sendFailed': '发送失败，请稍后再试',

    // Forgot Password Success
    'forgotPasswordSuccess.emailSent': '邮件已发送',
    'forgotPasswordSuccess.sentTo': '我们已向 {email} 发送了一封密码重置邮件。',
    'forgotPasswordSuccess.followInstructions': '请查收邮件并按照提示完成密码重置。',
    'forgotPasswordSuccess.checkSpam': '如果没有收到邮件，请检查垃圾邮件文件夹。',

    // Reset Password
    'resetPassword.title': '设置新密码',
    'resetPassword.invalidLink': '验证链接无效',
    'resetPassword.linkInvalid': '您的密码重置链接无效或已过期，请重新申请。',
    'resetPassword.applyAgain': '重新申请',
    'resetPassword.fillAllRequired': '请填写所有必填项',
    'resetPassword.passwordMismatch': '两次输入的密码不一致',
    'resetPassword.passwordTooShort': '密码长度至少6位',
    'resetPassword.setting': '设置中...',
    'resetPassword.setNewPassword': '设置新密码',
    'resetPassword.newPassword': '新密码',
    'resetPassword.atLeast6Chars': '至少6位字符',
    'resetPassword.confirmPassword': '确认密码',
    'resetPassword.enterPasswordAgain': '请再次输入密码',
    'resetPassword.resetSuccess': '密码重置成功',
    'resetPassword.resetFailed': '密码重置失败，请稍后再试',

    // Email Verify
    'emailVerify.verifying': '正在验证邮箱...',
    'emailVerify.verificationFailed': '验证失败',
    'emailVerify.verificationSuccess': '邮箱验证成功',
    'emailVerify.congratulations': '恭喜！您的邮箱已验证成功，现在可以登录了。',
    'emailVerify.goToLogin': '前往登录',
    'emailVerify.backToRegister': '返回注册',
    'emailVerify.backToLogin': '返回登录',
    'emailVerify.verifyingEmail': '正在验证邮箱...',
    'emailVerify.verificationFailedReason': '验证失败，请稍后再试',
    'emailVerify.verificationSuccessRedirect': '验证成功，正在跳转...',

    // Donation
    'donation.title': '捐助我们',
    'donation.description': '感谢您对桃花坛的支持！您的慷慨捐助将帮助我们更好地维护和运营社区。我们支持 TRC20（波场）和 ERC20（以太坊）两种 USDT 代币捐助。',
    'donation.donationNote': '捐助仅代表您对社区的支持，不提供任何特权，不影响社区免费生态。',
    'donation.readOnlyQuery': '全程只读查询',
    'donation.noPrivateKey': '无需私钥转账',
    'donation.autoRefresh': '秒自动刷新',
    'donation.trc20Title': '波场 TRC20 · USDT 实时流水',
    'donation.trc20Subtitle': '链上公开数据 · Tronscan',
    'donation.erc20Title': '以太坊 ERC20 · USDT 实时流水',
    'donation.erc20Subtitle': '链上公开数据 · Etherscan',
    'donation.paymentAddress': '收款地址',
    'donation.usdtBalance': 'USDT 余额',
    'donation.donate': '捐助',
    'donation.recentTransactions': '最近交易记录',
    'donation.refresh': '刷新',
    'donation.loadingTransactions': '正在加载交易数据...',
    'donation.retry': '重试',
    'donation.noTransactions': '暂无交易记录',
    'donation.type': '类型',
    'donation.amount': '金额',
    'donation.counterparty': '对方地址',
    'donation.time': '时间',
    'donation.txhash': 'TxHash',
    'donation.income': '收入',
    'donation.expense': '支出',
    'donation.addressCopied': '地址已复制',
    'donation.copyFailed': '复制失败，请手动复制地址',
    'donation.thankYouSupport': '感谢您的支持，欢迎加入我们共建社区',
    'donation.clickToPlay': '如未自动播放，请点击播放器播放',
    'donation.transactionDataNote': '所有交易数据均来自区块链公开信息，捐款时请仔细核对地址。',
    'donation.loadConfigFailed': '加载配置失败',

    // About Us
    'aboutUs.title': '关于我们',
    'aboutUs.fraudAlert': '防诈骗警示',
    'aboutUs.siteIntroduction': '网站简介',
    'aboutUs.ourMission': '我们的使命',
    'aboutUs.ourVision': '我们的愿景',
    'aboutUs.coreValues': '核心价值观',
    'aboutUs.freePolicy': '免费政策与收入来源',
    'aboutUs.siteFeatures': '网站功能',
    'aboutUs.teamIntroduction': '团队介绍',
    'aboutUs.contactUs': '联系我们',
    'aboutUs.joinUs': '加入我们',

    // Join Us
    'joinUs.title': '加入我们',
    'joinUs.subtitle': '成为桃花坛的一份子，共同建设温馨的交流社区',
    'joinUs.volunteer': '志愿者',
    'joinUs.channelOwner': '频道主',
    'joinUs.channelAdmin': '频道管理员',
    'joinUs.volunteerDesc': '帮助我们维护社区秩序，回答新手问题，参与社区活动组织。',
    'joinUs.channelOwnerDesc': '创建和管理特定技术领域的频道，组织专业内容，吸引相关领域的用户。',
    'joinUs.channelAdminDesc': '协助频道主管理频道，审核内容，组织活动，维护频道秩序。',
    'joinUs.applyProcess': '申请流程',
    'joinUs.submitApplication': '提交申请',
    'joinUs.submitApplicationDesc': '填写申请表，选择您感兴趣的角色和领域，并且提供您的联系方式用于联系。优先使用telegram作为联系方式',
    'joinUs.review': '审核评估',
    'joinUs.reviewDesc': '我们会对您的申请进行审核，评估您的技能和经验',
    'joinUs.approved': '审核通过',
    'joinUs.approvedDesc': '审核通过后会发邮件提醒您，同时账户权限会同步开通',
    'joinUs.faq': '常见问题',
    'joinUs.requirements': '加入我们需要什么条件？',
    'joinUs.requirementsAnswer': '我们欢迎对社区建设有热情、愿意为社区贡献的用户加入。具体条件会根据不同角色有所不同，但基本要求是：',
    'joinUs.volunteerBenefits': '成为志愿者有什么好处？',
    'joinUs.volunteerBenefitsAnswer': '成为志愿者不仅可以为社区做出贡献，还可以：',
    'joinUs.createBoard': '如何创建新的版块？',
    'joinUs.createBoardAnswer': '如果你想创建新的版块，需要先申请成为版块主。在申请时，你需要：',
    'joinUs.readyToJoin': '准备好加入我们了吗？',
    'joinUs.readyToJoinDesc': '无论你是经验丰富还是刚刚起步，只要你对社区有热情，都可以加入我们，一起建设更温暖的交流社区。',
    'joinUs.applyNow': '立即申请',
    'joinUs.applicationForm': '申请表单',
    'joinUs.name': '姓名',
    'joinUs.enterName': '请输入您的姓名',
    'joinUs.email': '邮箱',
    'joinUs.enterEmail': '请输入您的邮箱',
    'joinUs.telegram': 'Telegram账号',
    'joinUs.enterTelegram': '请输入您的Telegram账号，格式：@username',
    'joinUs.applyRole': '申请角色',
    'joinUs.selectRole': '请选择申请角色',
    'joinUs.applyReason': '申请理由',
    'joinUs.enterReason': '请简要说明您申请的理由和相关经验',
    'joinUs.agreeRules': '我同意遵守社区规则和行为准则',
    'joinUs.submitting': '提交中...',
    'joinUs.submitApplicationBtn': '提交申请',
    'joinUs.fillAllRequired': '请填写所有必填项',
    'joinUs.submitSuccess': '申请提交成功，我们会尽快审核并与您联系',
    'joinUs.submitFailed': '申请提交失败，请稍后重试',

    // Business Cooperation
    'businessCooperation.title': '商务合作',
    'businessCooperation.description': '我们欢迎各类与社区发展相关的商务合作',
    'businessCooperation.cooperationTypes': '合作类型',
    'businessCooperation.contactInfo': '联系方式',
    'businessCooperation.strategicPartners': '战略合作伙伴',
    'businessCooperation.loading': '加载中...',

    // Privacy Policy
    'privacyPolicy.title': '桃花坛隐私政策',
    'privacyPolicy.version': '隐私政策 v1.0',
    'privacyPolicy.lastUpdated': '最后更新',
    'privacyPolicy.loading': '加载中...',
    'privacyPolicy.fraudAlert': '防诈骗警示',
    'privacyPolicy.fraudAlertDesc': '尊敬的用户，请注意防范以下诈骗行为：',
    'privacyPolicy.scamType1': '冒充网站管理员或客服，以各种理由要求您提供账户密码、验证码或支付费用',
    'privacyPolicy.scamType2': '声称您的账户存在安全问题，需要进行"验证"或"升级"',
    'privacyPolicy.scamType3': '以"中奖"、"优惠活动"等名义诱导您点击链接或提供个人信息',
    'privacyPolicy.scamType4': '通过Telegram、微信等渠道发送可疑链接或文件',
    'privacyPolicy.scamType5': '要求您向指定账户转账或充值',
    'privacyPolicy.safetyTip': '安全提示：',
    'privacyPolicy.safetyTip1': '本网站不会通过电话、短信或邮件要求您提供密码或验证码',
    'privacyPolicy.safetyTip2': '请勿点击来源不明的链接或下载可疑文件',
    'privacyPolicy.safetyTip3': '请勿向任何个人或机构透露您的账户信息和密码',
    'privacyPolicy.safetyTip4': '如遇可疑情况，请通过官方渠道联系我们核实',
    'privacyPolicy.safetyTip5': '定期检查您的账户活动，如发现异常请立即联系我们',
    'privacyPolicy.section1': '一、引言',
    'privacyPolicy.intro1': '桃花坛（以下简称"我们"、"本平台"或"桃花坛"）致力于保护用户的隐私和个人信息安全。本隐私政策旨在清晰说明我们如何收集、使用、存储、保护和共享您的个人信息，以及您对这些信息所享有的权利。',
    'privacyPolicy.intro2': '当您访问我们的网站、使用我们的服务或与我们互动时，即表示您已阅读、理解并同意本隐私政策的条款。如果您不同意本政策，请不要使用我们的服务。',
    'privacyPolicy.intro3': '本隐私政策自2026年5月起生效，我们保留随时更新本政策的权利。',
    'privacyPolicy.section2': '二、我们收集的信息',
    'privacyPolicy.section2Desc': '我们收集的信息分为以下几类：',
    'privacyPolicy.section2_1': '2.1 账户注册信息',
    'privacyPolicy.section2_1Desc': '当您注册桃花坛账户时，我们会收集以下信息：',
    'privacyPolicy.username': '用户名',
    'privacyPolicy.usernameDesc': '您选择的显示名称',
    'privacyPolicy.email': '电子邮件地址',
    'privacyPolicy.emailDesc': '用于账户验证、密码重置和重要通知',
    'privacyPolicy.password': '密码',
    'privacyPolicy.passwordDesc': '经过加密存储的账户访问密码',
    'privacyPolicy.avatar': '头像（可选）',
    'privacyPolicy.avatarDesc': '您上传的个人头像图片',
    'privacyPolicy.bio': '个人简介（可选）',
    'privacyPolicy.bioDesc': '您填写的个人介绍信息',
    'privacyPolicy.telegram': 'Telegram账号（可选）',
    'privacyPolicy.telegramDesc': '用于接收通知和联系',
    'privacyPolicy.section2_2': '2.2 使用行为信息',
    'privacyPolicy.section2_2Desc': '当您使用我们的服务时，我们会自动收集以下信息：',
    'privacyPolicy.ip': 'IP地址',
    'privacyPolicy.ipDesc': '您的设备网络地址',
    'privacyPolicy.browser': '浏览器信息',
    'privacyPolicy.browserDesc': '浏览器类型、版本和语言设置',
    'privacyPolicy.device': '设备信息',
    'privacyPolicy.deviceDesc': '设备类型、操作系统版本',
    'privacyPolicy.accessLog': '访问记录',
    'privacyPolicy.accessLogDesc': '您访问的页面、点击的链接、停留时间',
    'privacyPolicy.interactionLog': '交互记录',
    'privacyPolicy.interactionLogDesc': '您发布的帖子、评论、点赞、私信等',
    'privacyPolicy.searchLog': '搜索记录',
    'privacyPolicy.searchLogDesc': '您在平台内的搜索关键词',
    'privacyPolicy.section2_3': '2.3 交易信息',
    'privacyPolicy.section2_3Desc': '如果您进行付费操作（如购买会员、捐赠等），我们会收集以下信息：',
    'privacyPolicy.amount': '交易金额',
    'privacyPolicy.amountDesc': '您支付的金额',
    'privacyPolicy.paymentMethod': '支付方式',
    'privacyPolicy.paymentMethodDesc': '您使用的支付渠道（如USDT TRC20/ERC20）',
    'privacyPolicy.transactionTime': '交易时间',
    'privacyPolicy.transactionTimeDesc': '交易发生的时间',
    'privacyPolicy.transactionStatus': '交易状态',
    'privacyPolicy.transactionStatusDesc': '交易是否成功',
    'privacyPolicy.walletAddress': '钱包地址（可选）',
    'privacyPolicy.walletAddressDesc': '您用于支付的加密货币钱包地址',
    'privacyPolicy.section2_4': '2.4 设备标识符和Cookie信息',
    'privacyPolicy.section2_4Desc': '我们使用Cookie和类似技术来增强您的使用体验：',
    'privacyPolicy.sessionCookie': '会话Cookie',
    'privacyPolicy.sessionCookieDesc': '用于维持您的登录状态',
    'privacyPolicy.preferenceCookie': '偏好Cookie',
    'privacyPolicy.preferenceCookieDesc': '记住您的语言、主题等偏好设置',
    'privacyPolicy.analyticsCookie': '分析Cookie',
    'privacyPolicy.analyticsCookieDesc': '帮助我们了解用户行为和优化服务',
    'privacyPolicy.deviceId': '设备标识符',
    'privacyPolicy.deviceIdDesc': '识别您的设备以提供一致的体验',
    'privacyPolicy.section2_5': '2.5 第三方服务信息',
    'privacyPolicy.section2_5Desc': '我们可能通过第三方服务收集以下信息：',
    'privacyPolicy.paymentProvider': '支付服务提供商',
    'privacyPolicy.paymentProviderDesc': '处理交易所需的必要信息',
    'privacyPolicy.analyticsProvider': '分析服务提供商',
    'privacyPolicy.analyticsProviderDesc': '网站使用数据统计',
    'privacyPolicy.securityProvider': '安全服务提供商',
    'privacyPolicy.securityProviderDesc': '防止欺诈和安全威胁',
    'privacyPolicy.section3': '三、我们如何使用您的信息',
    'privacyPolicy.section3Desc': '我们收集的信息主要用于以下目的：',
    'privacyPolicy.section3_1': '3.1 提供和维护服务',
    'privacyPolicy.section3_1Items': '创建和管理您的账户；提供访问权限和个性化体验；处理交易和支付；维护服务的正常运行',
    'privacyPolicy.section3_2': '3.2 沟通和支持',
    'privacyPolicy.section3_2Items': '发送账户验证邮件；回复您的咨询和请求；发送重要通知（如服务变更、安全提醒）；提供技术支持',
    'privacyPolicy.section3_3': '3.3 改进和优化',
    'privacyPolicy.section3_3Items': '分析用户行为以改进服务；优化网站性能和用户体验；开发新功能和服务；进行市场研究和分析',
    'privacyPolicy.section3_4': '3.4 安全和合规',
    'privacyPolicy.section3_4Items': '防止欺诈和滥用行为；检测和阻止安全威胁；遵守法律法规要求；保护平台和用户的安全',
    'privacyPolicy.section3_5': '3.5 个性化体验',
    'privacyPolicy.section3_5Items': '根据您的兴趣推荐内容；定制用户界面和功能；提供个性化的服务和优惠',
    'privacyPolicy.section4': '四、信息共享',
    'privacyPolicy.section4Desc': '我们非常重视您的隐私，严格限制信息共享。以下情况除外：',
    'privacyPolicy.section4_1': '4.1 必要的服务提供商',
    'privacyPolicy.section4_1Desc': '我们可能与以下服务提供商共享必要信息：',
    'privacyPolicy.paymentProcessor': '支付处理器',
    'privacyPolicy.paymentProcessorDesc': '处理支付交易所需的最小信息',
    'privacyPolicy.cloudProvider': '云服务提供商',
    'privacyPolicy.cloudProviderDesc': '托管服务和存储数据',
    'privacyPolicy.securityService': '安全服务提供商',
    'privacyPolicy.securityServiceDesc': '保护平台安全',
    'privacyPolicy.analyticsService': '分析服务提供商',
    'privacyPolicy.analyticsServiceDesc': '优化服务体验',
    'privacyPolicy.section4_1Note': '这些服务提供商仅能访问履行其职责所需的信息，且必须遵守严格的数据保护要求。',
    'privacyPolicy.section4_2': '4.2 法律要求',
    'privacyPolicy.section4_2Desc': '当我们收到法院传票、搜查令或其他法律要求时，可能会依法披露您的信息。',
    'privacyPolicy.section4_3': '4.3 保护权利和安全',
    'privacyPolicy.section4_3Desc': '为了保护桃花坛、用户或公众的权利、财产或安全，我们可能会披露必要的信息。',
    'privacyPolicy.section4_4': '4.4 业务变更',
    'privacyPolicy.section4_4Desc': '在合并、收购、资产出售或其他业务变更的情况下，您的信息可能会被转移给新的所有者。',
    'privacyPolicy.section4_5': '4.5 用户同意',
    'privacyPolicy.section4_5Desc': '在获得您明确同意的情况下，我们可能会共享您的信息。',
    'privacyPolicy.section5': '五、信息存储和安全',
    'privacyPolicy.section5_1': '5.1 数据存储',
    'privacyPolicy.section5_1Desc': '您的个人信息存储在安全的服务器上，采取了以下安全措施：',
    'privacyPolicy.encryptedStorage': '加密存储',
    'privacyPolicy.encryptedStorageDesc': '敏感信息（如密码）使用加密算法存储',
    'privacyPolicy.accessControl': '访问控制',
    'privacyPolicy.accessControlDesc': '只有授权人员才能访问您的信息',
    'privacyPolicy.regularBackup': '定期备份',
    'privacyPolicy.regularBackupDesc': '数据定期备份以防止丢失',
    'privacyPolicy.securityAudit': '安全审计',
    'privacyPolicy.securityAuditDesc': '定期进行安全审计和漏洞检测',
    'privacyPolicy.section5_2': '5.2 数据传输安全',
    'privacyPolicy.section5_2Desc': '我们使用HTTPS协议保护数据传输，确保您的信息在传输过程中不被截获或篡改。',
    'privacyPolicy.section5_3': '5.3 安全局限性',
    'privacyPolicy.section5_3Desc': '尽管我们采取了严格的安全措施，但没有任何系统是绝对安全的。我们无法保证您的信息完全不受未经授权的访问。请您也采取必要的措施保护自己的账户安全，如使用强密码、开启双重认证等。',
    'privacyPolicy.section6': '六、您的权利',
    'privacyPolicy.section6Desc': '根据适用法律，您享有以下权利：',
    'privacyPolicy.section6_1': '6.1 访问权',
    'privacyPolicy.section6_1Desc': '您有权访问我们持有的关于您的个人信息。',
    'privacyPolicy.section6_2': '6.2 更正权',
    'privacyPolicy.section6_2Desc': '如果您的个人信息不准确或不完整，您有权要求更正。',
    'privacyPolicy.section6_3': '6.3 删除权',
    'privacyPolicy.section6_3Desc': '在某些情况下，您有权要求删除您的个人信息。',
    'privacyPolicy.section6_4': '6.4 限制处理权',
    'privacyPolicy.section6_4Desc': '您有权要求限制对您个人信息的处理。',
    'privacyPolicy.section6_5': '6.5 数据可携带权',
    'privacyPolicy.section6_5Desc': '您有权获得您的个人信息副本，以便转移到其他服务提供商。',
    'privacyPolicy.section6_6': '6.6 撤回同意权',
    'privacyPolicy.section6_6Desc': '如果您之前同意我们处理您的信息，您有权撤回同意。',
    'privacyPolicy.section6_7': '6.7 行使权利的方式',
    'privacyPolicy.section6_7Desc': '如需行使上述权利，请通过我们的联系方式与我们联系。我们将在合理时间内回应您的请求。',
    'privacyPolicy.section7': '七、数据保留',
    'privacyPolicy.section7Desc': '我们仅在实现本隐私政策所述目的所需的时间内保留您的个人信息：',
    'privacyPolicy.accountInfo': '账户信息',
    'privacyPolicy.accountInfoDesc': '在账户存续期间保留，账户注销后保留必要时间以完成结算和合规要求',
    'privacyPolicy.transactionInfo': '交易信息',
    'privacyPolicy.transactionInfoDesc': '保留至少五年以满足税务和审计要求',
    'privacyPolicy.logData': '日志和使用数据',
    'privacyPolicy.logDataDesc': '保留不超过90天，除非法律要求更长时间',
    'privacyPolicy.userContent': '用户内容',
    'privacyPolicy.userContentDesc': '在您发布或删除前保留',
    'privacyPolicy.section7Note': '当信息不再需要时，我们将采取合理措施安全删除或匿名化处理这些信息。',
    'privacyPolicy.section8': '八、第三方链接和服务',
    'privacyPolicy.section8Desc': '我们的网站可能包含指向第三方网站或服务的链接。这些第三方服务有自己的隐私政策，我们不对其内容或隐私做法负责。',
    'privacyPolicy.section8Note': '我们建议您在访问第三方网站时仔细阅读其隐私政策。',
    'privacyPolicy.section9': '九、国际数据传输',
    'privacyPolicy.section9Desc': '我们的服务器位于中国大陆。如果您从其他国家或地区访问我们的服务，您的信息可能会被传输到中国大陆进行处理。',
    'privacyPolicy.section9Note': '我们确保所有国际数据传输都符合适用的数据保护法规。',
    'privacyPolicy.section10': '十、儿童隐私',
    'privacyPolicy.section10Desc': '我们的服务不面向13岁以下的儿童。我们不会故意收集儿童的个人信息。如果您发现我们无意中收集了儿童的信息，请联系我们，我们将立即删除。',
    'privacyPolicy.section11': '十一、Cookie政策',
    'privacyPolicy.section11Desc': '我们使用Cookie和类似技术来提供更好的用户体验。您可以通过浏览器设置管理Cookie偏好。',
    'privacyPolicy.necessaryCookie': '必要Cookie',
    'privacyPolicy.necessaryCookieDesc': '维持基本功能所需，无法禁用',
    'privacyPolicy.analyticsCookies': '分析Cookie',
    'privacyPolicy.analyticsCookiesDesc': '帮助我们了解用户行为',
    'privacyPolicy.preferenceCookies': '偏好Cookie',
    'privacyPolicy.preferenceCookiesDesc': '记住您的设置',
    'privacyPolicy.section12': '十二、隐私政策更新',
    'privacyPolicy.section12Desc': '我们可能不时更新本隐私政策。重大变更将通过网站公告或电子邮件通知您。',
    'privacyPolicy.section12Note': '我们鼓励您定期查看本隐私政策以了解最新信息。',
    'privacyPolicy.section13': '十三、联系我们',
    'privacyPolicy.section13Desc': '如果您对本隐私政策有任何问题或疑虑，请通过以下方式联系我们：',
    'privacyPolicy.contactEmail': '电子邮件',
    'privacyPolicy.contactTelegram': 'Telegram',
    'privacyPolicy.loadingText': '加载中...',

    // Terms of Service
    'termsOfService.title': '桃花坛服务协议',
    'termsOfService.version': '服务协议 v1.0',
    'termsOfService.lastUpdated': '最后更新日期',
    'termsOfService.back': '返回',
    'termsOfService.welcome': '欢迎使用桃花坛（以下简称"本平台"、"本论坛"或"桃花坛"）。本协议是您与桃花坛之间的服务协议，请仔细阅读本协议的全部内容。本协议旨在明确双方的权利义务关系，特别是强调房主责任独立、平台不参与内容、真实性不做研判、用户自行承担风险等核心原则。',
    'termsOfService.section1': '一、服务概述',
    'termsOfService.section1_1': '1.1 桃花坛是一个在线交流平台，为用户提供包括但不限于以下服务：',
    'termsOfService.section1_1Items': '创建和管理私人房间，进行私密交流；购买房间会员，享受专属权益；发布和浏览帖子、评论；参与社区讨论和互动；使用站内消息系统进行沟通；捐赠和打赏功能；网络存储空间服务；其他增值服务',
    'termsOfService.section1_2': '1.2 本平台提供的服务可能会根据业务发展进行调整和优化，桃花坛保留随时变更、暂停或终止部分或全部服务的权利，但会提前通知用户。',
    'termsOfService.section1_3': '1.3 本平台的部分功能可能需要付费使用，付费功能的具体说明和价格以平台公示为准。',
    'termsOfService.section1_4': '1.4 本平台遵循平等、自愿、公平、诚实信用的原则为用户提供服务。',
    'termsOfService.section2': '二、用户注册与账号管理',
    'termsOfService.section2_1': '2.1 用户在使用本平台服务前，需要注册一个账号。注册时需提供真实、准确、完整的信息，包括但不限于用户名、邮箱地址等必要信息。',
    'termsOfService.section2_2': '2.2 用户应妥善保管自己的账号和密码，对使用账号进行的所有活动负责。任何因账号泄露、被盗用或密码丢失导致的损失，由用户自行承担。',
    'termsOfService.section2_3': '2.3 用户不得将账号转让、出租、出借、出售或以其他方式许可他人使用。如发现账号异常使用或存在安全风险，桃花坛有权暂停或终止该账号的使用。',
    'termsOfService.section2_4': '2.4 用户应确保所提供的联系方式（如邮箱、手机号）准确有效，以便接收重要通知和验证信息。如联系方式变更，应及时更新。',
    'termsOfService.section2_5': '2.5 用户理解并同意，桃花坛有权对用户的注册信息进行审核，并有权要求用户补充提供身份证明或其他材料以完成实名认证。',
    'termsOfService.section2_6': '2.6 用户账号被暂停或终止后，将无法使用该账号登录本平台，已付费的服务可能无法获得退款。',
    'termsOfService.section3': '三、用户权利与义务',
    'termsOfService.section3_1': '3.1 用户有权在遵守本协议的前提下使用本平台提供的各项服务。',
    'termsOfService.section3_2': '3.2 用户有权对自己发布的内容进行编辑、修改或删除，但应承担相应责任。',
    'termsOfService.section3_3': '3.3 用户应遵守国家法律法规、社会公德和公序良俗，不得利用本平台进行任何违法违规活动。',
    'termsOfService.section3_4': '3.4 用户在本平台发布的内容必须符合法律法规和本协议的规定，不得包含以下内容：',
    'termsOfService.section3_4Items': '违反国家法律法规、行政法规或规章的内容；危害国家安全、泄露国家秘密、颠覆国家政权、破坏国家统一的内容；损害国家荣誉和利益的内容；煽动民族仇恨、民族歧视，破坏民族团结的内容；破坏国家宗教政策，宣扬邪教和封建迷信的内容；散布谣言，扰乱社会秩序，破坏社会稳定的内容；涉及色情、暴力、恐怖、赌博、毒品等违法违规内容；侮辱、诽谤他人，侵害他人合法权益的内容；侵犯他人知识产权、隐私权、名誉权、肖像权等合法权益的内容；虚假信息、误导性陈述或诈骗内容；恶意攻击、诽谤、侮辱他人的内容；含有商业广告性质的内容（除非经平台许可）；垃圾信息、 spam 内容；其他违反公序良俗或本平台规定的内容',
    'termsOfService.section3_5': '3.5 用户应尊重其他用户的权利，不得干扰他人正常使用本平台服务，不得进行任何骚扰、恐吓、欺诈或其他恶意行为。',
    'termsOfService.section3_6': '3.6 用户不得对本平台进行任何形式的破解、反编译、逆向工程，不得试图绕过或破解本平台的安全措施。',
    'termsOfService.section3_7': '3.7 用户不得利用本平台从事洗钱、套现、转移非法资金等违法犯罪活动。',
    'termsOfService.section4': '四、内容责任声明（核心条款）',
    'termsOfService.section4_1': '4.1 房主责任独立原则（最重要条款）',
    'termsOfService.section4_1Desc': '本平台仅提供技术服务和平台支持，不参与、不介入房主与用户之间的任何关系或交易。房主在其创建的房间内发表的言论、观点、信息、分析、建议等均为房主个人行为，与本平台无关。房主应对其在房间内发布的所有内容承担全部法律责任，包括但不限于民事责任、行政责任或刑事责任。',
    'termsOfService.section4_2': '4.2 平台不参与内容原则',
    'termsOfService.section4_2Desc': '本平台对房主或其他用户在房间内发表的任何言论、观点、信息、分析、建议等不参与、不干预、不编辑。平台仅提供技术手段保障内容的正常展示和传输，不对内容本身负责。本平台不认可、保证或支持任何用户在平台上发表的内容。',
    'termsOfService.section4_3': '4.3 真实性不做研判原则',
    'termsOfService.section4_3Desc': '本平台对用户发表的内容真实性、准确性、完整性、可靠性不做任何判断、验证、审查或保证。用户应自行甄别信息真伪，谨慎对待各类信息，特别是投资建议、分析观点、预测信息等。本平台不对因信息不实导致的任何损失、纠纷或后果承担责任。',
    'termsOfService.section4_4': '4.4 不提供任何保证原则',
    'termsOfService.section4_4Desc': '本平台不保证用户发表的内容准确、完整、及时、可靠或适用于任何特定目的。用户使用内容时应自行评估其适用性、可靠性和风险。本平台按"现状"提供服务，不提供任何明示或暗示的保证。',
    'termsOfService.section4_5': '4.5 投资建议免责声明（特别重要）',
    'termsOfService.section4_5Desc': '本平台内可能存在各类投资建议、分析观点、市场评论、预测信息等内容。用户应明白任何投资均存在风险，包括但不限于本金损失的风险。投资决策由用户自行做出，盈亏完全由用户自负。本平台不对任何投资损失、盈利或亏损承担责任，也不提供任何形式的投资咨询服务。任何自称能提供投资收益保证或预测市场走势的内容均为虚假信息。',
    'termsOfService.section4_6': '4.6 交易双方责任原则',
    'termsOfService.section4_6Desc': '本平台上的交易（包括但不限于会员购买、付费服务等）仅在用户与本平台之间进行，不涉及第三方房主与用户的直接交易（除非另有说明）。对于任何因交易产生的纠纷，应由交易双方自行解决，本平台不承担任何责任。',
    'termsOfService.section4_7': '4.7 第三方内容免责',
    'termsOfService.section4_7Desc': '本平台可能包含第三方提供的内容、广告或链接。对于第三方内容的真实性、合法性、安全性、可靠性，本平台不承担任何责任。用户访问第三方内容、广告或链接时应自行承担风险，仔细阅读并遵守第三方条款。',
    'termsOfService.section5': '五、风险承担',
    'termsOfService.section5_1': '5.1 用户自行承担全部风险（核心条款）',
    'termsOfService.section5_1Desc': '用户在使用本平台服务过程中，因房主言论、其他用户行为或内容引起的任何风险、损失、纠纷、法律责任等，均由用户自行承担，与本平台无关。用户承诺并保证不会因使用本平台服务而向本平台提出任何形式的索赔、诉讼或要求。',
    'termsOfService.section5_2': '5.2 信息安全风险',
    'termsOfService.section5_2Desc': '用户应自行保护个人信息安全，采取必要的安全措施防止个人信息泄露、被盗或被滥用。本平台不承担因用户个人信息泄露、被盗或被滥用导致的任何损失，包括但不限于财产损失、名誉损失等。',
    'termsOfService.section5_3': '5.3 交易风险',
    'termsOfService.section5_3Desc': '用户在本平台进行的任何交易（包括但不限于会员购买、捐赠、打赏、付费服务等）均存在风险，包括但不限于支付失败、交易延迟，资金损失等风险。用户应仔细确认交易信息，谨慎进行交易，认真阅读交易条款。本平台不对交易过程中的任何损失、纠纷承担责任。',
    'termsOfService.section5_4': '5.4 服务中断风险',
    'termsOfService.section5_4Desc': '本平台可能因技术故障、系统维护、升级、网络攻击、第三方服务中断、不可抗力等原因导致服务暂时不可用、数据丢失或延迟。用户应理解并接受这种风险，本平台不对由此造成的任何损失、直接或间接损害承担责任。',
    'termsOfService.section5_5': '5.5 内容风险',
    'termsOfService.section5_5Desc': '用户可能因接触本平台上的某些内容而产生心理、情绪或经济上的不适或损失。用户应自行评估内容的适用性，必要时寻求专业建议。本平台不对因接触平台内容导致的任何损失或不适承担责任。',
    'termsOfService.section5_6': '5.6 法律风险',
    'termsOfService.section5_6Desc': '用户应自行了解并遵守所在地区关于网络言论、投资交易的相关法律法规。因违反法律法规导致的法律责任，由用户自行承担。',
    'termsOfService.section6': '六、服务费用与支付',
    'termsOfService.section6_1': '6.1 本平台提供的部分服务可能需要支付费用，具体费用标准以平台公示为准。用户使用付费服务即表示同意按照公示的费用标准支付费用。',
    'termsOfService.section6_2': '6.2 用户应按照平台规定的方式支付费用。支持多种支付方式，包括但不限于USDT（TRC20、ERC20）等加密货币支付。支付完成后，费用不予退还，除非另有明确规定或法律法规要求。',
    'termsOfService.section6_3': '6.3 用户应对其支付行为负责，确保支付账户有足够的资金或信用额度。因支付账户余额不足、支付被拒绝等导致的交易失败，由用户自行承担责任。',
    'termsOfService.section6_4': '6.4 本平台保留随时调整服务费用的权利，调整后的费用标准将在平台公示。对于已支付费用的服务，不受影响。',
    'termsOfService.section6_5': '6.5 因加密货币价格波动可能导致服务费用的实际价值发生变化，本平台不对加密货币价格波动导致的任何损失承担责任。',
    'termsOfService.section6_6': '6.6 用户理解并同意，加密货币交易具有不可逆性，一旦完成转账，原则上无法撤销或退款。如需退款，应按照平台退款政策申请。',
    'termsOfService.section7': '七、平台权利与义务',
    'termsOfService.section7_1': '7.1 本平台有权根据法律法规要求、主管部门指令或自身判断，删除、屏蔽、隐藏违规内容或限制、暂停、终止违规用户使用服务。',
    'termsOfService.section7_2': '7.2 本平台有权对用户发布的内容进行审核、编辑、修改或删除，但不对审核的完整性和及时性做出保证。',
    'termsOfService.section7_3': '7.3 本平台有义务维护平台的正常运行，采取合理的安全措施保障用户数据安全，但不对数据丢失、安全事件等承担绝对责任。',
    'termsOfService.section7_4': '7.4 本平台有义务向用户提供必要的技术支持和服务说明，但不对服务质量做出绝对保证。',
    'termsOfService.section7_5': '7.5 本平台有权制定、修改平台规则、管理规定等规范性文件，用户应遵守这些规定。',
    'termsOfService.section7_6': '7.6 本平台有权在必要时对平台进行维护、升级或调整，可能导致服务暂时中断。',
    'termsOfService.section8': '八、隐私政策',
    'termsOfService.section8_1': '8.1 本平台尊重用户隐私，将按照隐私政策的规定收集、使用、存储，保护用户的个人信息。',
    'termsOfService.section8_2': '8.2 用户应仔细阅读隐私政策，了解个人信息的收集范围、使用方式，保护措施等内容。',
    'termsOfService.section8_3': '8.3 用户使用本平台服务即表示同意桃花坛按照隐私政策处理用户的个人信息。',
    'termsOfService.section9': '九、知识产权',
    'termsOfService.section9_1': '9.1 本平台的名称、标识、设计、界面、功能等知识产权归桃花坛所有，未经授权不得使用、复制、修改或传播。',
    'termsOfService.section9_2': '9.2 用户在本平台发布的内容（包括文字、图片、音频、视频等），用户保留其知识产权，但授予本平台在全球范围内免费、非独占的使用权，用于平台的运营和展示。',
    'termsOfService.section9_3': '9.3 如用户发现本平台上有侵犯自己知识产权的内容，可以向本平台提交侵权通知，本平台将根据法律法规进行处理。',
    'termsOfService.section10': '十、免责声明',
    'termsOfService.section10_1': '10.1 本平台对因不可抗力、第三方原因或其他非本平台过错导致的服务中断、数据丢失、系统故障、交易失败等不承担责任。',
    'termsOfService.section10_2': '10.2 本平台不对用户在使用服务过程中产生的任何间接损失、附带损失、后果性损失、惩罚性赔偿或精神损害赔偿承担责任，即使本平台已被告知可能发生此类损失的可能性。',
    'termsOfService.section10_3': '10.3 本平台不对用户之间因使用服务产生的任何纠纷、争议或损失承担责任。用户之间的纠纷应由双方自行解决。',
    'termsOfService.section10_4': '10.4 本平台不对因网络原因、黑客攻击、系统故障、第三方服务中断等导致的任何损失承担责任。',
    'termsOfService.section10_5': '10.5 本协议中的免责声明在法律允许的最大范围内有效。',
    'termsOfService.section11': '十一、协议修改与终止',
    'termsOfService.section11_1': '11.1 本平台保留随时修改本协议的权利。修改后的协议将在平台公告，修改内容自公告之日起生效。',
    'termsOfService.section11_2': '11.2 用户继续使用服务即视为同意修改后的协议。如果用户不同意修改后的协议，应停止使用本平台服务。',
    'termsOfService.section11_3': '11.3 用户可以随时停止使用本平台服务，账号注销后服务终止。',
    'termsOfService.section11_4': '11.4 本平台有权根据法律法规、主管部门要求或本协议规定终止向用户提供部分或全部服务，无需承担违约责任。',
    'termsOfService.section11_5': '11.5 账号被终止后，用户仍应对其在终止前发布的内容承担责任。',
    'termsOfService.section12': '十二、法律适用与争议解决',
    'termsOfService.section12_1': '12.1 本协议的订立、执行和解释均适用中华人民共和国法律。',
    'termsOfService.section12_2': '12.2 因本协议引起的或与本协议有关的任何争议，双方应首先通过友好协商解决；协商不成的，任何一方均可向有管辖权的人民法院提起诉讼。',
    'termsOfService.section13': '十三、其他条款',
    'termsOfService.section13_1': '13.1 本协议的解释权归桃花坛所有。',
    'termsOfService.section13_2': '13.2 本协议未尽事宜，参照国家法律法规、行业惯例和平台其他规定执行。',
    'termsOfService.section13_3': '13.3 如本协议与国家法律法规冲突，以法律法规为准，但不影响其他条款的效力。',
    'termsOfService.section13_4': '13.4 本协议各条款的标题仅为阅读方便，不影响条款内容的解释。',
    'termsOfService.importantNotice': '重要提示（请务必仔细阅读）',
    'termsOfService.importantNoticeDesc': '请您在使用本平台服务前仔细阅读本协议的全部内容。通过点击"同意"按钮或使用本平台服务，即表示您已阅读、理解并同意遵守本协议的全部条款。',
    'termsOfService.importantNoticeDesc2': '本协议旨在明确平台与用户之间的权利义务关系，特别是强调以下核心原则：',
    'termsOfService.importantNoticeItems': '房主责任独立：房主在房间内发表的任何言论均为个人行为，与平台无关；平台不参与内容：平台不对用户发表的内容负责；真实性不做研判：平台不验证信息的真实性；投资风险自担：任何投资决策由用户自行做出，盈亏自负；用户自行承担所有风险：因使用平台服务产生的任何损失由用户自行承担',
    'termsOfService.importantNoticeDesc3': '如果您不同意本协议的任何条款，请不要使用本平台服务。',

    // Admin Additional
    'admin.loadDataFailed': '加载数据失败',
    'admin.loadUsersFailed': '加载用户列表失败',
    'admin.loadUserGroupsFailed': '加载用户组列表失败',
    'admin.userGroupUpdated': '用户组更新成功',
    'admin.userGroupUpdateFailed': '用户组更新失败',
    'admin.userGroupCreated': '用户组创建成功',
    'admin.userGroupCreateFailed': '用户组创建失败',
    'admin.userGroupDeleted': '用户组删除成功',
    'admin.userGroupDeleteFailed': '用户组删除失败',
    'admin.saveUserGroupFailed': '保存用户组失败',
    'admin.userDeleted': '用户已删除',
    'admin.userDeleteFailed': '删除用户失败',
    'admin.userSaved': '用户信息已保存',
    'admin.saveFailed': '保存失败',
    'admin.loadCategoriesFailed': '加载版块列表失败',
    'admin.categoryUpdated': '版块已更新',
    'admin.categoryCreated': '版块已创建',
    'admin.categoryDeleted': '版块已删除',
    'admin.categoryDeleteFailed': '删除版块失败',
    'admin.categorySaveFailed': '保存版块失败',
    'admin.categoryShow': '版块已显示',
    'admin.categoryHide': '版块已隐藏',
    'admin.loadPostsFailed': '加载帖子列表失败',
    'admin.postDeleted': '帖子已删除',
    'admin.postDeleteFailed': '删除帖子失败',
    'admin.postMoved': '移动帖子成功',
    'admin.postMoveFailed': '移动失败',
    'admin.essenceSuccess': '设为精华成功',
    'admin.essenceCancel': '取消精华成功',
    'admin.loadSensitiveWordsFailed': '加载敏感词列表失败',
    'admin.sensitiveWordAdded': '添加敏感词成功',
    'admin.sensitiveWordAddFailed': '添加敏感词失败',
    'admin.sensitiveWordDeleted': '删除敏感词成功',
    'admin.sensitiveWordDeleteFailed': '删除敏感词失败',
    'admin.sensitiveWordsImported': '成功导入 {count} 个敏感词',
    'admin.sensitiveWordsImportFailed': '批量导入失败',
    'admin.loadConfigFailed': '加载配置失败',
    'admin.configUpdated': '配置更新成功',
    'admin.configUpdateFailed': '配置更新失败',
    'admin.loadAdSpotsFailed': '加载广告位失败',
    'admin.loadAdsFailed': '加载广告列表失败',
    'admin.adSpotOrderUpdated': '广告位排序已更新',
    'admin.adSpotOrderUpdateFailed': '更新广告位排序失败',
    'admin.adOrderUpdated': '广告排序已更新',
    'admin.adOrderUpdateFailed': '更新排序失败，请重试',
    'admin.adUpdated': '更新广告成功',
    'admin.adCreated': '添加广告成功',
    'admin.adSaveFailed': '保存广告失败',
    'admin.adDeleted': '删除广告成功',
    'admin.adDeleteFailed': '删除广告失败',
    'admin.totalUsers': '总用户数',
    'admin.totalPosts': '总帖子数',
    'admin.todayPosts': '今日帖子数',
    'admin.totalReplies': '回复总数',
    'admin.searchUsers': '搜索用户...',
    'admin.search': '搜索',
    'admin.saveChanges': '保存修改',
    'admin.confirmDelete': '确认删除',
    'admin.confirmDeleteUser': '确定要删除该用户吗？此操作不可恢复！',
    'admin.confirmDeleteUserGroup': '确定要删除这个用户组吗？此操作不可恢复！',
    'admin.confirmDeleteCategory': '确定要删除这个版块吗？此操作不可恢复！',
    'admin.confirmDeletePost': '确定要删除这篇帖子吗？此操作不可恢复！',
    'admin.confirmDeleteSensitiveWord': '确定要删除该敏感词吗？此操作不可恢复！',
    'admin.confirmDeleteAd': '确定要删除这个广告吗？此操作不可恢复！',
    'admin.confirmOperation': '确认操作',
    'admin.confirmBan': '确定要禁用该用户吗？',
    'admin.confirmUnban': '确定要启用该用户吗？',
    'admin.confirm': '确认',
    'admin.confirmMove': '确认移动',
    'admin.users': '用户',
    'admin.userGroups': '用户组',
    'admin.noUserGroup': '无用户组',
    'admin.createTime': '创建时间',
    'admin.noUserGroups': '暂无用户组',
    'admin.createFirstUserGroup': '创建第一个用户组',
    'admin.createUserGroup': '创建用户组',
    'admin.editUserGroup': '编辑用户组',
    'admin.userGroupName': '用户组名称',
    'admin.enterUserGroupName': '请输入用户组名称',
    'admin.userGroupDescription': '用户组描述',
    'admin.enterUserGroupDescription': '请输入用户组描述',
    'admin.selectRoleTip': '选择其他用户组作为对应的角色分类，或者留空表示无对应角色',
    'admin.createChannel': '创建频道',
    'admin.auditPosts': '审核帖子',
    'admin.appointRole': '任命用户角色',
    'admin.category': '版块',
    'admin.posts': '帖子',
    'admin.noDescription': '暂无描述',
    'admin.show': '显示',
    'admin.hide': '隐藏',
    'admin.addCategory': '添加版块',
    'admin.editCategory': '编辑版块',
    'admin.addPost': '添加帖子',
    'admin.searchPosts': '搜索帖子...',
    'admin.normal': '正常',
    'admin.deleted': '已删除',
    'admin.searchSensitiveWords': '搜索敏感词...',
    'admin.add': '添加',
    'admin.noSensitiveWords': '暂无敏感词',
    'admin.configsTotal': '配置项总数',
    'admin.loading': '加载中...',
    'admin.today': '今天有新的活动等着您处理',
    'admin.selectOrCreateRoom': '选择房间进行管理或创建新房间',
    
    // Admin Master Specific
    'admin.welcomeBack': '欢迎回来，{name}！',
    'admin.adminControlPanel': '这是您的社区管理控制台，今天有新的活动等着您处理',
    'admin.dashboardStats': '数据趋势',
    'admin.realTime': '实时刷新',
    'admin.minutes5': '5分钟',
    'admin.hour1': '1小时',
    'admin.hours24': '24小时',
    'admin.days7': '7天',
    'admin.autoRefresh': '每5秒自动刷新',
    'admin.autoRefreshMinutes': '每{minutes}分钟自动刷新',
    'admin.autoRefreshHour': '每小时自动刷新',
    'admin.autoRefreshDay': '每天自动刷新',
    'admin.autoRefreshWeek': '每{days}天自动刷新',
    'admin.userManagement': '用户管理',
    'admin.searchUserPlaceholder': '搜索用户...',
    'admin.reset': '重置',
    'admin.refresh': '刷新',
    'admin.userId': '用户ID',
    'admin.email': '邮箱',
    'admin.emailVerification': '邮箱验证',
    'admin.registerTime': '注册时间',
    'admin.verified': '已验证',
    'admin.notVerified': '未验证',
    'admin.deleteUser': '删除用户',
    'admin.disableUser': '禁用用户',
    'admin.enableUser': '启用用户',
    'admin.editEmail': '修改邮箱',
    'admin.sendVerificationEmail': '发送验证邮件',
    'admin.sendResetPasswordEmail': '发送重置密码邮件',
    'admin.cancel': '取消',
    'admin.confirmDelete': '确认删除',
    'admin.confirmDeleteUser': '确定要删除该用户吗？此操作不可恢复！',
    'admin.confirmDeleteUserGroup': '确定要删除这个用户组吗？此操作不可恢复！',
    'admin.confirmDeleteCategory': '确定要删除这个版块吗？此操作不可恢复！',
    'admin.confirmDeletePost': '确定要删除这篇帖子吗？此操作不可恢复！',
    'admin.confirmDeleteSensitiveWord': '确定要删除该敏感词吗？此操作不可恢复！',
    'admin.confirmDeleteAd': '确定要删除这个广告吗？此操作不可恢复！',
    'admin.confirmOperation': '确认操作',
    'admin.confirmBan': '确定要禁用该用户吗？',
    'admin.confirmUnban': '确定要启用该用户吗？',
    'admin.confirmSendVerification': '确定要发送验证邮件吗？',
    'admin.confirmSendResetPassword': '确定要发送重置密码邮件吗？',
    'admin.userDisabled': '用户已禁用',
    'admin.userEnabled': '用户已启用',
    'admin.verificationEmailSent': '验证邮件已发送',
    'admin.resetPasswordEmailSent': '重置密码邮件已发送',
    'admin.userDeleted': '用户已删除',
    'admin.deleteUserFailed': '删除用户失败',
    'admin.userInfoSaved': '用户信息已保存',
    'admin.saveFailed': '保存失败',
    'admin.searchPosts': '搜索帖子...',
    'admin.movePost': '移动帖子',
    'admin.movePostToCategory': '移动帖子到其他版块',
    'admin.currentCategory': '当前版块',
    'admin.targetCategory': '目标版块',
    'admin.globalTop': '全站置顶',
    'admin.categoryTop': '板块置顶',
    'admin.cancelTop': '取消置顶',
    'admin.globalTopSuccess': '全站置顶成功',
    'admin.categoryTopSuccess': '板块置顶成功',
    'admin.cancelTopSuccess': '取消置顶成功',
    'admin.setEssence': '设为精华',
    'admin.cancelEssence': '取消精华',
    'admin.setEssenceSuccess': '设为精华成功',
    'admin.cancelEssenceSuccess': '取消精华成功',
    'admin.postDeleted': '删除帖子成功',
    'admin.deletePostFailed': '删除帖子失败',
    'admin.movePostSuccess': '移动帖子成功',
    'admin.movePostFailed': '移动失败',
    'admin.categoryManagement': '版块管理',
    'admin.addCategory': '添加版块',
    'admin.editCategory': '编辑版块',
    'admin.categoryName': '版块名称',
    'admin.enterCategoryName': '请输入版块名称',
    'admin.categoryDescription': '版块描述',
    'admin.enterCategoryDescription': '请输入版块描述',
    'admin.topLevelCategory': '顶级版块',
    'admin.parentCategory': '上级版块',
    'admin.noParent': '无上级',
    'admin.sortOrder': '排序',
    'admin.enterSortOrder': '请输入排序号',
    'admin.createCategory': '创建版块',
    'admin.updateCategory': '更新版块',
    'admin.categoryCreated': '创建版块成功',
    'admin.categoryUpdated': '更新版块成功',
    'admin.saveCategoryFailed': '保存版块失败',
    'admin.categoryShown': '版块已显示',
    'admin.categoryHidden': '版块已隐藏',
    'admin.categoryDeleted': '删除版块成功',
    'admin.deleteCategoryFailed': '删除版块失败',
    'admin.sensitiveWordLibrary': '敏感词库',
    'admin.addSensitiveWord': '添加敏感词',
    'admin.batchImport': '批量导入',
    'admin.word': '敏感词',
    'admin.enterWord': '请输入敏感词',
    'admin.enterWordPlaceholder': '请输入敏感词',
    'admin.batchImportPlaceholder': '批量导入敏感词，每行一个',
    'admin.sensitiveWordAdded': '添加敏感词成功',
    'admin.addSensitiveWordFailed': '添加敏感词失败',
    'admin.batchImportFailed': '批量导入失败',
    'admin.importedSuccess': '成功导入 {count} 个敏感词',
    'admin.sensitiveWordDeleted': '删除敏感词成功',
    'admin.deleteSensitiveWordFailed': '删除敏感词失败',
    'admin.siteConfig': '站点配置',
    'admin.loadConfigFailed': '加载配置失败',
    'admin.configUpdated': '配置更新成功',
    'admin.updateConfigFailed': '配置更新失败',
    'admin.adManagement': '广告管理',
    'admin.loadAdSpotsFailed': '加载广告位失败',
    'admin.adSpotOrderUpdated': '广告位排序已更新',
    'admin.updateAdSpotOrderFailed': '更新广告位排序失败',
    'admin.loadAdsFailed': '加载广告列表失败',
    'admin.adNotFound': '广告未找到',
    'admin.adOrderUpdated': '广告排序已更新',
    'admin.updateAdOrderFailed': '更新排序失败，请重试',
    'admin.adUpdated': '更新广告成功',
    'admin.adCreated': '添加广告成功',
    'admin.saveAdFailed': '保存广告失败',
    'admin.adDeleted': '删除广告成功',
    'admin.deleteAdFailed': '删除广告失败',
    'admin.adSpot': '广告位',
    'admin.adTitle': '标题',
    'admin.adContent': '内容',
    'admin.adImage': '图片',
    'admin.adLink': '链接',
    'admin.adType': '类型',
    'admin.adImageType': '图片',
    'admin.adCodeType': '代码',
    'admin.adStartTime': '开始时间',
    'admin.adEndTime': '结束时间',
    'admin.adActive': '启用',
    'admin.adInactive': '禁用',
    'admin.adSortOrder': '排序',
    'admin.addAd': '添加广告',
    'admin.editAd': '编辑广告',
    'admin.deleteAd': '删除广告',
    'admin.operationLogs': '操作日志',
  },
  en: {
    // Common
    'common.loading': 'Loading...',
    'common.submit': 'Submit',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.save': 'Save',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.reset': 'Reset',
    'common.export': 'Export',
    'common.import': 'Import',
    'common.refresh': 'Refresh',
    'common.close': 'Close',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.success': 'Success',
    'common.error': 'Error',
    'common.warning': 'Warning',
    'common.info': 'Info',
    'common.noData': 'No Data',
    'common.optional': 'Optional',
    'common.required': 'Required',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.createdAt': 'Created At',
    'common.updatedAt': 'Updated At',
    'common.total': 'Total',
    'common.page': 'Page',
    'common.perPage': 'Items/Page',
    'common.all': 'All',
    'common.more': 'More',
    'common.view': 'View',
    'common.download': 'Download',
    'common.upload': 'Upload',
    'common.copy': 'Copy',
    'common.copied': 'Copied',
    'common.selectAll': 'Select All',
    'common.deselectAll': 'Deselect All',
    'common.pleaseSelect': 'Please Select',
    'common.pleaseEnter': 'Please Enter',
    'common.confirmDelete': 'Confirm Delete?',
    'common.deleteSuccess': 'Deleted Successfully',
    'common.saveSuccess': 'Saved Successfully',
    'common.operationFailed': 'Operation Failed',
    'common.networkError': 'Network Error',
    'common.searchPlaceholder': 'Search posts...',

    // Navigation
    'nav.home': 'Home',
    'nav.posts': 'Posts',
    'nav.rooms': 'VIP Rooms',
    'nav.storage': 'Cloud Storage',
    'nav.messages': 'Messages',
    'nav.userCenter': 'Profile',
    'nav.login': 'Login',
    'nav.register': 'Register',
    'nav.logout': 'Logout',
    'nav.admin': 'Admin',
    'nav.master': 'Master Admin',
    'nav.about': 'About Us',
    'nav.contact': 'Contact Us',
    'nav.faq': 'Help Center',
    'nav.privacy': 'Privacy Policy',
    'nav.terms': 'Terms of Service',
    'nav.donation': 'Donate',
    'nav.joinUs': 'Join Us',
    'nav.chat': 'Chat Room',
    'nav.createPost': 'Create Post',
    'nav.categories': 'Categories',
    'nav.roomManagement': 'My Room Management',

    // User Center
    'userCenter.title': 'User Center',
    'userCenter.profile': 'Profile',
    'userCenter.changePassword': 'Change Password',
    'userCenter.nickname': 'Nickname',
    'userCenter.signature': 'Signature',
    'userCenter.avatar': 'Avatar',
    'userCenter.changeAvatar': 'Change Avatar',
    'userCenter.oldPassword': 'Old Password',
    'userCenter.newPassword': 'New Password',
    'userCenter.confirmPassword': 'Confirm Password',
    'userCenter.passwordMismatch': 'Passwords do not match',
    'userCenter.updateSuccess': 'Updated Successfully',
    'userCenter.stats': 'Statistics',
    'userCenter.level': 'Level Info',
    'userCenter.growth': 'Growth',
    'userCenter.points': 'Points',
    'userCenter.myPosts': 'My Posts',
    'userCenter.myReplies': 'My Replies',
    'userCenter.achievements': 'Achievements',
    'userCenter.currentLevel': 'Current Level',
    'userCenter.nextLevel': 'Next Level',
    'userCenter.experience': 'Experience',
    'userCenter.pointsBalance': 'Points Balance',
    'userCenter.postCount': 'Posts',
    'userCenter.replyCount': 'Replies',
    'userCenter.likesReceived': 'Likes Received',
    'userCenter.viewsReceived': 'Views Received',

    // Home
    'home.welcome': 'Welcome to Peach Blossom Forum',
    'home.latestPosts': 'Latest Posts',
    'home.hotPosts': 'Hot Posts',
    'home.categories': 'Categories',
    'home.viewAll': 'View All',
    'home.postsCount': 'Posts',
    'home.repliesCount': 'Replies',
    'home.lastActivity': 'Last Activity',
    'home.noPosts': 'No Posts Yet',
    'home.lastReply': 'Last Reply',
    'home.previousPage': 'Previous',
    'home.nextPage': 'Next',
    'home.pageInfo': 'Page {page} / {totalPages}',

    // Category
    'category.title': 'Category',
    'category.postsCount': 'Posts',
    'category.noPosts': 'No posts in this category yet',

    // Posts
    'posts.title': 'Posts',
    'posts.create': 'Create Post',
    'posts.edit': 'Edit Post',
    'posts.delete': 'Delete Post',
    'posts.content': 'Content',
    'posts.category': 'Category',
    'posts.author': 'Author',
    'posts.createdAt': 'Posted At',
    'posts.updatedAt': 'Updated At',
    'posts.views': 'Views',
    'posts.likes': 'Likes',
    'posts.replies': 'Replies',
    'posts.like': 'Like',
    'posts.reply': 'Reply',
    'posts.share': 'Share',
    'posts.report': 'Report',
    'posts.top': 'Pin',
    'posts.globalTop': 'Global Pin',
    'posts.categoryTop': 'Category Pin',
    'posts.essence': 'Essence',
    'posts.locked': 'Locked',
    'posts.pending': 'Pending',
    'posts.approved': 'Approved',
    'posts.rejected': 'Rejected',
    'posts.publish': 'Publish',
    'posts.draft': 'Draft',
    'posts.preview': 'Preview',
    'posts.titlePlaceholder': 'Enter post title',
    'posts.contentPlaceholder': 'Enter post content, supports Markdown format',
    'posts.selectCategory': 'Select Category',
    'posts.minLength': 'Minimum 10 characters',
    'posts.maxLength': 'Maximum 50000 characters',
    'posts.publishSuccess': 'Published Successfully',
    'posts.updateSuccess': 'Updated Successfully',
    'posts.deleteSuccess': 'Deleted Successfully',
    'posts.deleteConfirm': 'Are you sure you want to delete this post?',
    'posts.notFound': 'Post Not Found',
    'posts.noPermission': 'No Permission',
    'posts.replyPlaceholder': 'Write your reply...',
    'posts.uploadImage': 'Upload Image',
    'posts.uploadAttachment': 'Upload Attachment',
    'posts.imageTooLarge': 'Image size cannot exceed 5MB',
    'posts.unsupportedFormat': 'Unsupported image format',
    'posts.canDelete': 'You can delete your own replies',
    'posts.adminCanDelete': 'Admins can delete any replies',

    // Chat
    'chat.title': 'Chat Room',
    'chat.send': 'Send',
    'chat.placeholder': 'Type a message...',
    'chat.image': 'Image',
    'chat.file': 'File',
    'chat.recall': 'Recall',
    'chat.delete': 'Delete',
    'chat.more': 'Load More',
    'chat.noMessages': 'No Messages Yet',
    'chat.sendSuccess': 'Sent Successfully',
    'chat.sendFailed': 'Send Failed',
    'chat.uploadFailed': 'Upload Failed',

    // Room
    'room.title': 'VIP Rooms',
    'room.join': 'Join Room',
    'room.leave': 'Leave Room',
    'room.create': 'Create Room',
    'room.manage': 'Manage Room',
    'room.owner': 'Owner',
    'room.members': 'Members',
    'room.memberCount': 'Member Count',
    'room.maxMembers': 'Max Members',
    'room.description': 'Description',
    'room.settings': 'Room Settings',
    'room.qrCode': 'Payment QR Code',
    'room.price': 'Price',
    'room.period': 'Period',
    'room.monthly': 'Monthly',
    'room.quarterly': 'Quarterly',
    'room.yearly': 'Yearly',
    'room.permanent': 'Permanent',
    'room.joinRoom': 'Join Room',
    'room.exitRoom': 'Exit Room',
    'room.roomFull': 'Room is Full',
    'room.notOwner': 'Not the Owner',
    'room.alreadyJoined': 'Already Joined',
    'room.notJoined': 'Not Joined Yet',
    'room.pause': 'Pause',
    'room.resume': 'Resume',
    'room.paused': 'Paused',
    'room.active': 'Active',
    'room.hidden': 'Hidden',
    'room.public': 'Public',
    'room.private': 'Private',
    'room.description_': 'Join paid rooms and communicate with like-minded friends. Contact admin for owner permissions',
    'room.noRooms': 'No VIP Rooms Yet',
    'room.running': 'Running',
    'room.closed': 'Closed',
    'room.oneTime': 'One-time',
    'room.perMonth': '/month',
    'room.perYear': '/year',
    'room.permanentShort': '/permanent',
    'room.notMember': 'You are not a member of {roomName}',
    'room.purchaseToJoin': 'Please purchase membership to enter the room',
    'room.purchase': 'Purchase Membership',
    'room.joinSuccess': 'Joined Successfully',
    'room.leaveSuccess': 'Left Room',
    'room.myRoom': 'My Room Management',
    'room.myRoomList': 'My Room List',
    'room.noRoomYet': 'No rooms created yet',
    'room.createFirst': 'Create First Room',
    'room.createNew': 'Create New Room',
    'room.roomName': 'Room Name',
    'room.roomDescription': 'Room Description',
    'room.price': 'Price',
    'room.priceInUsdt': 'Price (USDT)',
    'room.priceType': 'Pricing Model',
    'room.maxMembers': 'Max Members',
    'room.unlimited': 'Unlimited',
    'room.paymentQrCode': 'Payment QR Code',
    'room.uploadQrCode': 'Upload QR Code',
    'room.trc20Address': 'TRC20 Address',
    'room.erc20Address': 'ERC20 Address',
    'room.enterTrc20': 'Enter your TRC20 wallet address',
    'room.enterErc20': 'Enter your ERC20 wallet address',
    'room.trc20Network': 'TRC20 (TRON Network)',
    'room.erc20Network': 'ERC20 (Ethereum Network)',
    'room.enableRoom': 'Enable Room',
    'room.hideRoom': 'Hide Room',
    'room.expiration': 'Room Validity',
    'room.expiredAt': 'Expires At',
    'room.renewRoom': 'Renew Room',
    'room.renewal': 'Renewal',
    'room.confirmRenew': 'Confirm Renewal',
    'room.renewalPeriod': 'Select Renewal Period',
    'room.paused': 'Paused',
    'room.basicSettings': 'Basic Settings',
    'room.memberManagement': 'Member Management',
    'room.banManagement': 'Ban Management',
    'room.apiKeys': 'API Keys',
    'room.aiConfig': 'AI Configuration',
    'room.owner': 'Owner',
    'room.admin': 'Admin',
    'room.member': 'Member',
    'room.currentMembers': 'Current Members',
    'room.memberList': 'Member List',
    'room.openedMembers': 'Active Members',
    'room.memberList_': 'Active Members List',
    'room.noMembers': 'No Members Yet',
    'room.noMemberRecords': 'No Member Records',
    'room.banList': 'Ban List',
    'room.addBan': 'Add Ban',
    'room.noBanRecords': 'No Ban Records',
    'room.userId': 'User ID',
    'room.reason': 'Reason',
    'room.noReason': 'No Reason',
    'room.duration': 'Duration (hours)',
    'room.permanentBan': 'Permanent',
    'room.leaveEmptyForPermanent': 'Leave empty for permanent ban',
    'room.createdAt': 'Created At',
    'room.lastUsed': 'Last Used',
    'room.notUsed': 'Not Used',
    'room.apiKeyGenerated': 'API Key Generated',
    'room.saveApiKey': 'Please keep your API key safe, it will only be shown once',
    'room.keyName': 'Key Name',
    'room.enterKeyName': 'Enter key name',
    'room.generateKey': 'Generate Key',
    'room.noApiKeys': 'No API Keys Yet',
    'room.aiAssistant': 'AI Assistant Config',
    'room.aiComingSoon': 'AI Assistant Coming Soon',
    'room.welcomeRoom': 'Welcome to Room Management',
    'room.selectOrCreate': 'Select a room to manage or create a new one',
    'room.createRoomPermission': 'Room Creation Permission',
    'room.purchaseRoomPackage': 'To create rooms, you need to purchase a room package first. The package includes room creation privileges and exclusive management features.',
    'room.roomPackage': 'Room Package',
    'room.createUnlimitedRooms': 'Create unlimited rooms',
    'room.manageMembers': 'Manage room members and bans',
    'room.aiAssistantConfig': 'AI Assistant configuration',
    'room.selectPeriod': 'Select Package Period:',
    'room.payNote': 'Payment Notes',
    'room.platformFee': 'Platform only charges room opening fee',
    'room.directPayment': 'User payments go directly to your TRC20 wallet, platform does not take a cut',
    'room.autoActivate': 'Access is activated automatically after payment',
    'room.purchaseNow': 'Purchase Now',
    'room.roomExpired': 'Room Expired',
    'room.forever': 'Permanent',
    'room.searchMembers': 'Search members...',
    'room.orderId': 'Order ID',
    'room.amount': 'Amount',
    'room.activated': 'Activated',
    'room.activatedAt': 'Activated At',
    'room.expiredAt_': 'Expires At',
    'room.cancel': 'Cancel',
    'room.confirm': 'Confirm',
    'room.enter': 'Enter',
    'room.enterRoomName': 'Enter room name',
    'room.enterRoomDesc': 'Enter room description',
    'room.enterUserId': 'Enter user ID',
    'room.enterBanReason': 'Enter ban reason',
    'room.saveQrCode': 'Optional: provide QR code for easier payment',
    'room.supportFormat': 'Supports JPG/PNG, max 5MB',
    'room.clickToUpload': 'Click to upload',
    'room.dragOrClick': 'or click to upload',
    'room.imagePreview': 'Preview',
    'room.sending': 'Sending...',
    'room.firstMessage': 'No messages yet, send the first message!',
    'room.joinRoomTip': 'Join Room',
    'room.exitRoomTip': 'Exit Room',

    // Membership
    'membership.title': 'Membership',
    'membership.purchase': 'Purchase',
    'membership.upgrade': 'Upgrade',
    'membership.renew': 'Renew',
    'membership.expired': 'Expired',
    'membership.current': 'Current Plan',
    'membership.expiresAt': 'Expires At',
    'membership.daysLeft': 'Days Left',
    'membership.vip': 'VIP Member',
    'membership.svip': 'SVIP Member',
    'membership.unlimited': 'Unlimited',

    // Payment
    'payment.title': 'Payment',
    'payment.method': 'Payment Method',
    'payment.address': 'Payment Address',
    'payment.amount': 'Amount',
    'payment.orderNo': 'Order No',
    'payment.status': 'Status',
    'payment.pending': 'Pending',
    'payment.paid': 'Paid',
    'payment.failed': 'Failed',
    'payment.cancelled': 'Cancelled',
    'payment.expired': 'Expired',
    'payment.confirm': 'Confirm Payment',
    'payment.confirmManual': 'Manual Confirm',
    'payment.waiting': 'Waiting',
    'payment.processing': 'Processing',
    'payment.success': 'Payment Successful',
    'payment.failed_': 'Payment Failed',
    'payment.timeout': 'Timeout',
    'payment.retry': 'Retry',
    'payment.copyAddress': 'Copy Address',
    'payment.scanQrCode': 'Scan QR Code',
    'payment.manualConfirmTip': 'Please transfer to the specified address and click confirm',

    // Storage
    'storage.title': 'Cloud Storage',
    'storage.used': 'Used',
    'storage.total': 'Total',
    'storage.files': 'My Files',
    'storage.purchase': 'Purchase Space',
    'storage.upgrade': 'Upgrade',
    'storage.delete': 'Delete',
    'storage.download': 'Download',
    'storage.rename': 'Rename',
    'storage.share': 'Share',
    'storage.noFiles': 'No Files Yet',
    'storage.fileName': 'File Name',
    'storage.fileSize': 'File Size',
    'storage.uploadTime': 'Upload Time',
    'storage.selectFile': 'Select File',
    'storage.dragFile': 'Drag file here',
    'storage.orClick': 'or click to upload',

    // Admin
    'admin.title': 'Admin Panel',
    'admin.dashboard': 'Dashboard',
    'admin.users': 'Users',
    'admin.posts': 'Posts',
    'admin.categories': 'Categories',
    'admin.rooms': 'Rooms',
    'admin.orders': 'Orders',
    'admin.ads': 'Ads',
    'admin.config': 'Site Config',
    'admin.logs': 'Logs',
    'admin.chat': 'Chat Admin',
    'admin.sensitiveWords': 'Sensitive Words',
    'admin.userGroups': 'User Groups',
    'admin.packages': 'Packages',
    'admin.reconciliation': 'Reconciliation',
    'admin.pendingOrders': 'Pending Orders',
    'admin.completedOrders': 'Completed Orders',
    'admin.allOrders': 'All Orders',
    'admin.roomOrders': 'Room Orders',
    'admin.membershipOrders': 'Membership Orders',
    'admin.storageOrders': 'Storage Orders',
    'admin.donationOrders': 'Donation Orders',
    'admin.expansionOrders': 'Expansion Orders',
    'admin.roomMembershipOrders': 'Room Membership Orders',
    'admin.roomCreationOrders': 'Room Creation Orders',
    'admin.createRoom': 'Create Room',
    'admin.pauseRoom': 'Pause Room',
    'admin.resumeRoom': 'Resume Room',
    'admin.deleteRoom': 'Delete Room',
    'admin.viewRoom': 'View Room',
    'admin.editRoom': 'Edit Room',
    'admin.approve': 'Approve',
    'admin.reject': 'Reject',
    'admin.batchApprove': 'Batch Approve',
    'admin.batchReject': 'Batch Reject',
    'admin.searchUser': 'Search User',
    'admin.searchPost': 'Search Post',
    'admin.searchOrder': 'Search Order',
    'admin.totalUsers': 'Total Users',
    'admin.totalPosts': 'Total Posts',
    'admin.totalOrders': 'Total Orders',
    'admin.todayOrders': 'Today Orders',
    'admin.todayRevenue': 'Today Revenue',
    'admin.activeUsers': 'Active Users',
    'admin.newUsers': 'New Users',
    'admin.userId': 'User ID',
    'admin.username': 'Username',
    'admin.email': 'Email',
    'admin.role': 'Role',
    'admin.banned': 'Banned',
    'admin.active': 'Active',
    'admin.ban': 'Ban',
    'admin.unban': 'Unban',
    'admin.banReason': 'Ban Reason',
    'admin.banSuccess': 'Banned Successfully',
    'admin.unbanSuccess': 'Unbanned Successfully',
    'admin.batchOperation': 'Batch Operation',
    'admin.selectUsers': 'Select Users',
    'admin.selectPosts': 'Select Posts',
    'admin.selectOrders': 'Select Orders',
    'admin.confirmBatch': 'Confirm Batch Operation',
    'admin.batchSuccess': 'Batch Operation Successful',
    'admin.word': 'Word',
    'admin.wordType': 'Word Type',
    'admin.replaceWith': 'Replace With',
    'admin.addWord': 'Add Word',
    'admin.importWords': 'Import',
    'admin.exportWords': 'Export',
    'admin.groupName': 'Group Name',
    'admin.groupDescription': 'Group Description',
    'admin.permissions': 'Permissions',
    'admin.addGroup': 'Add Group',
    'admin.editGroup': 'Edit Group',
    'admin.deleteGroup': 'Delete Group',
    'admin.setGroup': 'Set Group',
    'admin.updateGroup': 'Update Group',
    'admin.cannotUpdateSelf': 'Cannot update your own user group',

    // Admin Dashboard
    'admin.quickActions': 'Quick Actions',
    'admin.manageUsers': 'Manage user accounts',
    'admin.managePosts': 'Manage post content',
    'admin.manageCategories': 'Manage forum categories',
    'admin.manageSensitiveWords': 'Manage sensitive word list',
    'admin.configureSite': 'Configure site settings',
    'admin.totalReplies': 'Total Replies',
    'admin.totalCategories': 'Total Categories',

    // Admin Users
    'admin.searchUserId': 'Search by User ID, username, nickname, email...',
    'admin.searching': 'Searching...',
    'admin.foundResults': 'Found {count} results',
    'admin.noMatchingUsers': 'No matching users found',
    'admin.noUsers': 'No user data',
    'admin.noUserGroup': 'No User Group',
    'admin.registerTime': 'Register Time',

    // Admin Posts
    'admin.postTitle': 'Title',
    'admin.movePost': 'Move post to another category',
    'admin.globalTop': 'Global Pin',
    'admin.categoryTop': 'Category Pin',
    'admin.untop': 'Unpin',
    'admin.globalTopSuccess': 'Global pinned successfully',
    'admin.categoryTopSuccess': 'Category pinned successfully',
    'admin.untopSuccess': 'Unpinned successfully',
    'admin.essence': 'Essence',
    'admin.cancelEssence': 'Cancel Essence',
    'admin.setEssenceSuccess': 'Set as essence successfully',
    'admin.cancelEssenceSuccess': 'Cancelled essence successfully',
    'admin.confirmDeletePost': 'Are you sure you want to delete this post? This action cannot be undone.',
    'admin.currentCategory': 'Current Category',
    'admin.selectTargetCategory': 'Select Target Category',
    'admin.moveSuccess': 'Post moved successfully',
    'admin.moveFailed': 'Move failed',
    'admin.views': 'Views',
    'admin.replies': 'Replies',
    'admin.likes': 'Likes',
    'admin.normal': 'Normal',
    'admin.deleted': 'Deleted',

    // Admin Categories
    'admin.categoryName': 'Category Name',
    'admin.categoryDescription': 'Category Description',
    'admin.createCategory': 'Create Category',
    'admin.asTopLevelCategory': 'As Top Level Category',
    'admin.asChildCategory': 'As child category of {name}',
    'admin.description': 'Description',
    'admin.postCount': 'Posts',
    'admin.show': 'Show',
    'admin.hide': 'Hide',
    'admin.confirmDeleteCategory': 'Are you sure you want to delete this category? This action cannot be undone.',

    // Room Management
    'admin.roomManagement': 'Room Management',
    'admin.selectRoom': 'Select Room',
    'admin.searchOwner': 'Search owner...',
    'admin.noRooms': 'No rooms',
    'admin.noRoomsCreated': 'You have not created any rooms yet',
    'admin.createNewRoom': 'Create New Room',
    'admin.roomSettings': 'Room Settings',
    'admin.banManagement': 'Ban Management',
    'admin.apiKeys': 'API Keys',
    'admin.aiConfig': 'AI Config',
    'admin.memberManagement': 'Member Management',
    'admin.roomName': 'Room Name',
    'admin.priceUsdt': 'Price (USDT)',
    'admin.pricingModel': 'Pricing Model',
    'admin.monthly': 'Monthly',
    'admin.yearly': 'Yearly',
    'admin.oneTime': 'One-time',
    'admin.roomDescription': 'Room Description',
    'admin.trc20Address': 'TRC20 Address',
    'admin.erc20Address': 'ERC20 Address',
    'admin.maxMembers': 'Max Members',
    'admin.paymentQrCode': 'Payment QR Code',
    'admin.upload': 'Upload',
    'admin.jpgPngMax5mb': 'JPG/PNG, max 5MB',
    'admin.enableRoom': 'Enable Room',
    'admin.hideRoom': 'Hide Room',
    'admin.roomValidityPeriod': 'Room Validity Period',
    'admin.expireTime': 'Expire Time',
    'admin.roomExpired': 'Room Expired',
    'admin.renew': 'Renew',
    'admin.banList': 'Ban List',
    'admin.addBan': 'Add Ban',
    'admin.noBanUsers': 'No banned users',
    'admin.userId': 'User ID',
    'admin.reason': 'Reason',
    'admin.expire': 'Expire',
    'admin.permanentBan': 'Permanent Ban',
    'admin.removeBan': 'Remove Ban',
    'admin.aiFeatureConfig': 'AI Feature Config',
    'admin.aiAssistant': 'AI Assistant',
    'admin.aiAssistantDesc': 'Configure AI assistant for members to provide intelligent Q&A services. AI features can help members answer questions and increase room activity.',
    'admin.enableAiAssistant': 'Enable AI Assistant',
    'admin.enableAiAssistantDesc': 'After enabling, members can use AI Q&A in the room',
    'admin.aiResponseRateLimit': 'AI Response Rate Limit',
    'admin.aiRateLow': 'Low (10 requests per user per hour)',
    'admin.aiRateMedium': 'Medium (30 requests per user per hour)',
    'admin.aiRateHigh': 'High (60 requests per user per hour)',
    'admin.aiRateUnlimited': 'Unlimited',
    'admin.aiModelSelection': 'AI Model Selection',
    'admin.gpt35': 'GPT-3.5 (Fast Response)',
    'admin.gpt4': 'GPT-4 (Higher Quality)',
    'admin.claude': 'Claude (Balanced)',
    'admin.systemPrompt': 'System Prompt',
    'admin.systemPromptPlaceholder': 'Define AI assistant role and behavior...',
    'admin.manualEnableMember': 'Manually Enable Member',
    'admin.currentMembers': 'Current Members',
    'admin.enabledMembers': 'Enabled Members',
    'admin.searchMembers': 'Search members...',
    'admin.noMembers': 'No members',
    'admin.enabledMemberList': 'Enabled Member List',
    'admin.refresh': 'Refresh',
    'admin.noEnabledMembers': 'No enabled member records',
    'admin.notes': 'Notes',
    'admin.manualEnableNote': 'Manual enable is for special cases such as: chain transfer without automatic arrival, promotional activities, gifted membership, etc. Enable records will be logged.',
    'admin.apiKeyManagement': 'API Key Management',
    'admin.createKey': 'Create Key',
    'admin.newApiKey': 'New API Key Created',
    'admin.saveKeyNote': 'Please save the key carefully, you will not be able to view the full key after closing',
    'admin.noApiKeys': 'No API keys',
    'admin.enable': 'Enable',
    'admin.disable': 'Disable',
    'admin.resetKey': 'Reset Key',
    'admin.externalApiDoc': 'External API Documentation',
    'admin.requestUrl': 'Request URL',
    'admin.headers': 'Headers',
    'admin.body': 'Body (JSON)',
    'admin.roomId': 'Room ID',
    'admin.messageContent': 'Message Content',
    'admin.welcomeRoomManagement': 'Welcome to Room Management',
    'admin.noRoomsPleaseCreate': 'No rooms yet, please create one first',
    'admin.goCreateRoom': 'Create a Room',
    'admin.enterRoomName': 'Enter room name',
    'admin.enterRoomDescription': 'Enter room description',
    'admin.enterTrc20Address': 'Enter your TRC20 wallet address',
    'admin.trc20Network': 'TRC20 (TRON Network)',
    'admin.enterErc20Address': 'Enter your ERC20 wallet address',
    'admin.erc20Network': 'ERC20 (Ethereum Network)',
    'admin.noLimit': 'No limit',
    'admin.maxMemberLimit': 'Maximum member limit for room',
    'admin.uploadQrCodeImage': 'Upload QR code image',
    'admin.optionalQrCode': 'Optional: provide QR code for easy payment',
    'admin.cancel': 'Cancel',
    'admin.createRoom': 'Create Room',
    'admin.banUser': 'Ban',
    'admin.enterUserId': 'Enter user ID',
    'admin.banReason': 'Ban Reason',
    'admin.enterBanReason': 'Enter ban reason',
    'admin.banDuration': 'Ban Duration (hours)',
    'admin.permanentBanNote': 'Leave empty for permanent ban',
    'admin.keyName': 'Key Name',
    'admin.enterKeyName': 'Enter key name (optional)',
    'admin.usernameOrEmail': 'Username/Email',
    'admin.enterUsernameOrEmail': 'Enter username or email to search',
    'admin.memberType': 'Member Type',
    'admin.normalMember': 'Normal Member',
    'admin.roomAdmin': 'Room Admin',
    'admin.expireTime2': 'Expire Time',
    'admin.permanentMember': 'Permanent Member',
    'admin.confirmEnable': 'Confirm Enable',
    'admin.renewRoom': 'Renew Room',
    'admin.selectRenewPeriod': 'Select Renew Period',
    'admin.quarterly': 'Quarterly',
    'admin.confirmRenew': 'Confirm Renew',

    // Pending Orders
    'admin.pendingOrders': 'Pending Orders',
    'admin.searchUsername': 'Search username...',
    'admin.type': 'Type',
    'admin.amount': 'Amount',
    'admin.txid': 'TXID',
    'admin.remainingTime': 'Remaining Time',
    'admin.createTime': 'Create Time',
    'admin.operation': 'Operation',
    'admin.completeOrder': 'Complete Order',
    'admin.refundOrder': 'Refund Order',
    'admin.selectPackageUpgrade': 'Select Package to Upgrade',
    'admin.orderId': 'Order ID',
    'admin.user': 'User',
    'admin.unknown': 'Unknown',
    'admin.enterActualSpace': 'Original order unpaid, please enter actual space size',
    'admin.selectPackage': 'Select Package',
    'admin.confirmOperation': 'Confirm Operation',
    'admin.timedOut': 'Timed Out',
    'admin.noPendingOrders': 'No pending orders',
    'admin.totalRecords': 'Total {total} records, Page {page} / {totalPages}',
    'admin.previousPage': 'Previous Page',
    'admin.nextPage': 'Next Page',
    'admin.orderCompleted': 'Completed',
    'admin.cancelOrder': 'Cancel Order',
    'admin.selectPackageToExpand': 'Select Package to Expand',

    // Admin Master
    'admin.dashboard': 'Dashboard',
    'admin.users': 'Users',
    'admin.categories': 'Categories',
    'admin.posts': 'Posts',
    'admin.sensitiveWordLibrary': 'Sensitive Words',
    'admin.chatManagement': 'Chat Admin',
    'admin.vipRoomManagement': 'VIP Rooms',
    'admin.orderManagement': 'Orders',
    'admin.expansionOrders': 'Expansion Orders',
    'admin.donationOrders': 'Donation Orders',
    'admin.roomOrders': 'Room Orders',
    'admin.experience': 'Experience',
    'admin.siteConfig': 'Site Config',
    'admin.adManagement': 'Ads',
    'admin.stats': 'Statistics',
    'admin.operationLogs': 'Logs',
    'admin.loadDataFailed': 'Failed to load data',
    'admin.loadUserListFailed': 'Failed to load user list',
    'admin.loadUserGroupListFailed': 'Failed to load user group list',
    'admin.updateUserGroupSuccess': 'User group updated successfully',
    'admin.createUserGroupSuccess': 'User group created successfully',
    'admin.saveUserGroupFailed': 'Failed to save user group',
    'admin.deleteUserGroupSuccess': 'User group deleted successfully',
    'admin.deleteUserGroupFailed': 'Failed to delete user group',

    // Auth
    'auth.login': 'Login',
    'auth.register': 'Register',
    'auth.logout': 'Logout',
    'auth.username': 'Username',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.forgotPassword': 'Forgot Password?',
    'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account?',
    'auth.registerNow': 'Register Now',
    'auth.loginNow': 'Login Now',
    'auth.rememberMe': 'Remember Me',
    'auth.verificationCode': 'Verification Code',
    'auth.sendCode': 'Send Code',
    'auth.codeSent': 'Code Sent',
    'auth.resetPassword': 'Reset Password',
    'auth.resetSuccess': 'Password Reset Successfully',
    'auth.resetFailed': 'Password Reset Failed',
    'auth.invalidCode': 'Invalid Code',
    'auth.codeExpired': 'Code Expired',
    'auth.loginSuccess': 'Login Successful',
    'auth.registerSuccess': 'Registered Successfully',
    'auth.logoutSuccess': 'Logged Out Successfully',
    'auth.invalidCredentials': 'Invalid username or password',
    'auth.emailExists': 'Email already registered',
    'auth.usernameExists': 'Username already taken',
    'auth.weakPassword': 'Password too weak',
    'auth.passwordTooShort': 'Password must be at least 6 characters',
    'auth.emailInvalid': 'Invalid email format',
    'auth.usernameInvalid': 'Invalid username format',
    'auth.usernameLength': 'Username must be at least 3 characters',
    'auth.pleaseLogin': 'Please login first',
    'auth.pleaseVerifyEmail': 'Please verify your email first',
    'auth.emailVerified': 'Email verified',
    'auth.emailNotVerified': 'Email not verified',
    'auth.verifyEmail': 'Verify Email',
    'auth.verifyEmailSuccess': 'Email Verified Successfully',
    'auth.verifyEmailFailed': 'Email Verification Failed',
    'auth.resendVerification': 'Resend Verification Email',
    'auth.verificationEmailSent': 'Verification Email Sent',
    'auth.verificationEmailSentTip': 'Please check your email and click the verification link',
    'auth.clickToVerify': 'Click here to send verification email',
    'auth.usernameOrEmail': 'Username or Email',
    'auth.enterUsernameOrEmail': 'Please enter username or email',
    'auth.enterPassword': 'Please enter password',
    'auth.loggingIn': 'Logging in...',
    'auth.loginFailed': 'Login failed',
    'auth.noAccountYet': "Don't have an account?",

    // Register
    'register.title': 'Register',
    'register.username': 'Username',
    'register.email': 'Email',
    'register.password': 'Password',
    'register.confirmPassword': 'Confirm Password',
    'register.enterUsername': 'Enter username (3-20 characters)',
    'register.enterEmail': 'Please enter email address',
    'register.enterPassword': 'Enter password (at least 6 characters)',
    'register.confirmPasswordPlaceholder': 'Please enter password again',
    'register.registering': 'Registering...',
    'register.hasAccount': 'Already have an account?',
    'register.loginNow': 'Login Now',
    'register.fillRequired': 'Please fill in all required fields',
    'register.passwordMismatch': 'Passwords do not match',
    'register.passwordTooShort': 'Password must be at least 6 characters',
    'register.success': 'Registration successful, please check your email to verify',
    'register.failed': 'Registration failed',
    'register.verificationEmailSent': 'Verification Email Sent',
    'register.sentVerificationEmail': 'We have sent a verification email to {email}',
    'register.checkEmail': 'Please check your email and click the verification link to complete registration',
    'register.willRedirect': 'Page will automatically redirect to login in {seconds} seconds...',
    'register.backToLogin': 'Back to Login',
    'register.sending': 'Sending...',
    'register.resendVerification': 'Resend Verification Email',
    'register.resendSuccess': 'Verification email resent, please check your inbox',
    'register.sendFailed': 'Send failed, please try again later',
    'register.usernameTip': '3-20 characters, letters, numbers, underscores',
    'register.passwordTip': 'At least 6 characters',

    // Posts
    'posts.title': 'Post',
    'posts.detail': 'Post Detail',
    'posts.floor': 'Floor',
    'posts.floorDetail': 'Sub-floor',
    'posts.op': 'OP',
    'posts.downloadCount': 'downloads',
    'posts.download': 'Download',
    'posts.edit': 'Edit',
    'posts.delete': 'Delete',
    'posts.replyComment': 'Reply to Comment',
    'posts.postReply': 'Post Reply',
    'posts.replyingTo': 'Replying to',
    'posts.cancel': 'Cancel',
    'posts.uploading': 'Uploading...',
    'posts.uploadImage': 'Upload Image',
    'posts.uploadAttachment': 'Upload Attachment',
    'posts.attachments': 'Attachments',
    'posts.attachmentsAdded': 'Attached Files',
    'posts.writeReply': 'Write your reply...',
    'posts.sending': 'Sending...',
    'posts.sendReply': 'Send Reply',
    'posts.loginToJoin': 'Login to join the discussion',
    'posts.noReplies': 'No replies yet',
    'posts.login': 'Login',
    'posts.confirmDelete': 'Confirm Delete',
    'posts.confirmDeletePost': 'Are you sure you want to delete this post? This action cannot be undone.',
    'posts.irrevocable': 'This action cannot be undone',
    'posts.replySuccess': 'Reply submitted successfully',
    'posts.replyFailed': 'Failed to submit reply',
    'posts.attachmentTooLarge': 'Attachment size cannot exceed 10MB',
    'posts.attachmentUploadSuccess': 'Attachment uploaded successfully',
    'posts.attachmentUploadFailed': 'Attachment upload failed',
    'posts.imageUploadSuccess': 'Image uploaded successfully',
    'posts.uploadFailed': 'Upload failed',
    'posts.deleteSuccess': 'Deleted successfully',
    'posts.deleteFailed': 'Failed to delete',
    'posts.confirmDeleteReply': 'Are you sure you want to delete this reply?',
    'posts.notFound': 'Post not found',

    // Create Post
    'createPost.title': 'Create New Post',
    'createPost.selectBoard': 'Select Board',
    'createPost.pleaseSelectBoard': 'Please select a board',
    'createPost.board': 'Board',
    'createPost.postTitle': 'Title',
    'createPost.pleaseEnterTitle': 'Please enter title (5-100 characters)',
    'createPost.titleLength': '(5-100 characters)',
    'createPost.content': 'Content',
    'createPost.pleaseEnterContent': 'Supports Markdown format, please enter post content (10-50000 characters)',
    'createPost.contentLength': '(10-50000 characters)',
    'createPost.markdownTip': 'Supports Markdown syntax: **bold**, *italic*, `code`, [link](url), etc',
    'createPost.attachedFiles': 'Attached Files',
    'createPost.cancel': 'Cancel',
    'createPost.publishing': 'Publishing...',
    'createPost.publishPost': 'Publish Post',
    'createPost.imageUploadSuccess': 'Image uploaded successfully',
    'createPost.uploadFailed': 'Upload failed',
    'createPost.uploading': 'Uploading...',
    'createPost.uploadImage': 'Upload Image',
    'createPost.uploadAttachment': 'Upload Attachment',
    'createPost.fileTooLarge': 'File size cannot exceed 100MB',
    'createPost.attachmentUploadSuccess': 'Attachment uploaded successfully',
    'createPost.enterTitle': 'Please enter title',
    'createPost.enterContent': 'Please enter content',
    'createPost.selectCategory': 'Please select a board',
    'createPost.postSuccess': 'Post published successfully',
    'createPost.postFailed': 'Failed to publish post',

    // Errors
    'error.network': 'Network error, please try again later',
    'error.server': 'Server error, please try again later',
    'error.notFound': 'Page Not Found',
    'error.unauthorized': 'Unauthorized, please login',
    'error.forbidden': 'Access Forbidden',
    'error.badRequest': 'Bad Request',
    'error.tooManyRequests': 'Too many requests, please try again later',
    'error.unknown': 'Unknown Error',

    // Footer
    'footer.copyright': '© 2024 Peach Blossom Forum. All Rights Reserved',
    'footer.madeWith': 'Made with ❤️',

    // Messages
    'messages.title': 'Messages',
    'messages.system': 'System Messages',
    'messages.comments': 'Comment Messages',
    'messages.likes': 'Like Messages',
    'messages.mentions': 'Mention Messages',
    'messages.at': 'Someone mentioned you in',
    'messages.mentioned': '',
    'messages.liked': ' liked your ',
    'messages.commented': ' commented on your ',
    'messages.reply': 'reply',
    'messages.post': 'post',
    'messages.read': 'Read',
    'messages.unread': 'Unread',
    'messages.markAllRead': 'Mark All Read',
    'messages.noMessages': 'No Messages Yet',
    'messages.clear': 'Clear Messages',

    // About
    'about.title': 'About Us',
    'about.description': 'Peach Blossom Forum is a community platform dedicated to technology sharing and communication',
    'about.contact': 'Contact Us',
    'about.email': 'Email',
    'about.phone': 'Phone',
    'about.address': 'Address',

    // Other
    'other.loading': 'Loading...',
    'other.noMore': 'No More',
    'other.loadingMore': 'Loading More...',
    'other.pullToRefresh': 'Pull to Refresh',
    'other.releaseToRefresh': 'Release to Refresh',
    'other.refreshing': 'Refreshing...',

    // User Profile
    'userProfile.title': 'User Profile',
    'userProfile.level': 'Level',
    'userProfile.points': 'Points',
    'userProfile.posts': 'Posts',
    'userProfile.replies': 'Replies',
    'userProfile.registeredAt': 'Registered',
    'userProfile.publishedPosts': 'Published Posts',
    'userProfile.noPosts': 'No posts yet',

    // Storage
    'storage.loadFailed': 'Failed to load data',
    'storage.selectFile': 'Please select a file first',
    'storage.fileTooLarge': 'File size cannot exceed 100MB',
    'storage.uploadSuccess': 'Uploaded successfully!',
    'storage.uploadFailed': 'Upload failed',
    'storage.deleteSuccess': 'Deleted successfully',
    'storage.deleteFailed': 'Delete failed',
    'storage.confirmPayment': 'Are you sure you want to confirm this payment?',
    'storage.paymentSuccess': 'Payment successful! You received',
    'storage.confirmPaymentFailed': 'Failed to confirm payment',

    // Edit Post
    'editPost.title': 'Edit Post',
    'editPost.backToPost': 'Back to Post',
    'editPost.postTitle': 'Title',
    'editPost.enterTitle': 'Please enter post title',
    'editPost.category': 'Category',
    'editPost.selectCategory': 'Please select a category',
    'editPost.content': 'Content',
    'editPost.enterContent': 'Please enter post content',
    'editPost.submitting': 'Submitting...',
    'editPost.saveChanges': 'Save Changes',
    'editPost.noPermission': 'No permission to edit this post',
    'editPost.fetchFailed': 'Failed to fetch data',
    'editPost.fillAll': 'Please fill in all post information',
    'editPost.editFailed': 'Edit failed',
    'editPost.notFound': 'Post not found',

    // Search Results
    'searchResults.title': 'Search Results',
    'searchResults.keyword': 'Keyword',
    'searchResults.resultsCount': '{total} results',
    'searchResults.previousPage': 'Previous',
    'searchResults.nextPage': 'Next',
    'searchResults.pageInfo': 'Page {page} / {totalPages}',
    'searchResults.noResults': 'No related posts found',

    // Forgot Password
    'forgotPassword.title': 'Forgot Password',
    'forgotPassword.enterEmail': 'Enter your registered email, we will send a password reset link',
    'forgotPassword.emailAddress': 'Email Address',
    'forgotPassword.enterRegisteredEmail': 'Please enter registered email',
    'forgotPassword.sending': 'Sending...',
    'forgotPassword.sendResetLink': 'Send Reset Link',
    'forgotPassword.rememberPassword': 'Remember your password?',
    'forgotPassword.backToLogin': 'Back to Login',
    'forgotPassword.enterEmailAddress': 'Please enter email address',
    'forgotPassword.resetEmailSent': 'Password reset email has been sent, please check your inbox',
    'forgotPassword.sendFailed': 'Send failed, please try again later',

    // Forgot Password Success
    'forgotPasswordSuccess.emailSent': 'Email Sent',
    'forgotPasswordSuccess.sentTo': 'We have sent a password reset email to {email}.',
    'forgotPasswordSuccess.followInstructions': 'Please check your email and follow the instructions to reset your password.',
    'forgotPasswordSuccess.checkSpam': 'If you did not receive the email, please check your spam folder.',

    // Reset Password
    'resetPassword.title': 'Set New Password',
    'resetPassword.invalidLink': 'Invalid Link',
    'resetPassword.linkInvalid': 'Your password reset link is invalid or has expired, please apply again.',
    'resetPassword.applyAgain': 'Apply Again',
    'resetPassword.fillAllRequired': 'Please fill in all required fields',
    'resetPassword.passwordMismatch': 'Passwords do not match',
    'resetPassword.passwordTooShort': 'Password must be at least 6 characters',
    'resetPassword.setting': 'Setting...',
    'resetPassword.setNewPassword': 'Set New Password',
    'resetPassword.newPassword': 'New Password',
    'resetPassword.atLeast6Chars': 'At least 6 characters',
    'resetPassword.confirmPassword': 'Confirm Password',
    'resetPassword.enterPasswordAgain': 'Please enter password again',
    'resetPassword.resetSuccess': 'Password reset successfully',
    'resetPassword.resetFailed': 'Password reset failed, please try again later',

    // Email Verify
    'emailVerify.verifying': 'Verifying email...',
    'emailVerify.verificationFailed': 'Verification Failed',
    'emailVerify.verificationSuccess': 'Email Verified Successfully',
    'emailVerify.congratulations': 'Congratulations! Your email has been verified successfully. You can now login.',
    'emailVerify.goToLogin': 'Go to Login',
    'emailVerify.backToRegister': 'Back to Register',
    'emailVerify.backToLogin': 'Back to Login',
    'emailVerify.verifyingEmail': 'Verifying email...',
    'emailVerify.verificationFailedReason': 'Verification failed, please try again later',
    'emailVerify.verificationSuccessRedirect': 'Verification successful, redirecting...',

    // Donation
    'donation.title': 'Donate',
    'donation.description': 'Thank you for your support! Your generous donation will help us better maintain and operate the community. We support both TRC20 (TRON) and ERC20 (Ethereum) USDT token donations.',
    'donation.donationNote': 'Donation only represents your support for the community, does not provide any privileges, and does not affect the free ecosystem.',
    'donation.readOnlyQuery': 'Read-only query only',
    'donation.noPrivateKey': 'No private key transfer needed',
    'donation.autoRefresh': 'seconds auto-refresh',
    'donation.trc20Title': 'TRON TRC20 · USDT Real-time Flow',
    'donation.trc20Subtitle': 'On-chain public data · Tronscan',
    'donation.erc20Title': 'Ethereum ERC20 · USDT Real-time Flow',
    'donation.erc20Subtitle': 'On-chain public data · Etherscan',
    'donation.paymentAddress': 'Payment Address',
    'donation.usdtBalance': 'USDT Balance',
    'donation.donate': 'Donate',
    'donation.recentTransactions': 'Recent Transactions',
    'donation.refresh': 'Refresh',
    'donation.loadingTransactions': 'Loading transaction data...',
    'donation.retry': 'Retry',
    'donation.noTransactions': 'No transaction records',
    'donation.type': 'Type',
    'donation.amount': 'Amount',
    'donation.counterparty': 'Counterparty',
    'donation.time': 'Time',
    'donation.txhash': 'TxHash',
    'donation.income': 'Income',
    'donation.expense': 'Expense',
    'donation.addressCopied': 'Address copied',
    'donation.copyFailed': 'Copy failed, please copy manually',
    'donation.thankYouSupport': 'Thank you for your support, welcome to join us in building the community',
    'donation.clickToPlay': 'If it does not auto-play, please click the player to play',
    'donation.transactionDataNote': 'All transaction data comes from blockchain public information. Please carefully verify the address when donating.',
    'donation.loadConfigFailed': 'Failed to load config',

    // About Us
    'aboutUs.title': 'About Us',
    'aboutUs.fraudAlert': 'Fraud Alert',
    'aboutUs.siteIntroduction': 'Site Introduction',
    'aboutUs.ourMission': 'Our Mission',
    'aboutUs.ourVision': 'Our Vision',
    'aboutUs.coreValues': 'Core Values',
    'aboutUs.freePolicy': 'Free Policy & Revenue Sources',
    'aboutUs.siteFeatures': 'Site Features',
    'aboutUs.teamIntroduction': 'Team Introduction',
    'aboutUs.contactUs': 'Contact Us',
    'aboutUs.joinUs': 'Join Us',

    // Join Us
    'joinUs.title': 'Join Us',
    'joinUs.subtitle': 'Become part of Peach Blossom Forum and build a warm community together',
    'joinUs.volunteer': 'Volunteer',
    'joinUs.channelOwner': 'Channel Owner',
    'joinUs.channelAdmin': 'Channel Admin',
    'joinUs.volunteerDesc': 'Help us maintain community order, answer questions from newcomers, and participate in community activities.',
    'joinUs.channelOwnerDesc': 'Create and manage channels in specific technical fields, organize professional content, and attract users in related fields.',
    'joinUs.channelAdminDesc': 'Assist channel owners in managing channels, reviewing content, organizing activities, and maintaining channel order.',
    'joinUs.applyProcess': 'Application Process',
    'joinUs.submitApplication': 'Submit Application',
    'joinUs.submitApplicationDesc': 'Fill out the application form, select the roles and fields you are interested in, and provide your contact information. Telegram is preferred.',
    'joinUs.review': 'Review & Evaluation',
    'joinUs.reviewDesc': 'We will review your application and evaluate your skills and experience',
    'joinUs.approved': 'Approved',
    'joinUs.approvedDesc': 'You will receive an email notification after approval, and account permissions will be synchronized',
    'joinUs.faq': 'FAQ',
    'joinUs.requirements': 'What are the requirements to join?',
    'joinUs.requirementsAnswer': 'We welcome users who are passionate about community building and willing to contribute to the community. Specific requirements vary by role, but basic requirements include:',
    'joinUs.volunteerBenefits': 'What are the benefits of becoming a volunteer?',
    'joinUs.volunteerBenefitsAnswer': 'Becoming a volunteer not only allows you to contribute to the community, but also:',
    'joinUs.createBoard': 'How to create a new board?',
    'joinUs.createBoardAnswer': 'If you want to create a new board, you need to first apply to become a board owner. When applying, you need to:',
    'joinUs.readyToJoin': 'Ready to join us?',
    'joinUs.readyToJoinDesc': 'Whether you are experienced or just starting out, as long as you have passion for the community, you can join us and build a warmer communication community together.',
    'joinUs.applyNow': 'Apply Now',
    'joinUs.applicationForm': 'Application Form',
    'joinUs.name': 'Name',
    'joinUs.enterName': 'Please enter your name',
    'joinUs.email': 'Email',
    'joinUs.enterEmail': 'Please enter your email',
    'joinUs.telegram': 'Telegram Account',
    'joinUs.enterTelegram': 'Please enter your Telegram account, format: @username',
    'joinUs.applyRole': 'Apply for Role',
    'joinUs.selectRole': 'Please select a role',
    'joinUs.applyReason': 'Application Reason',
    'joinUs.enterReason': 'Please briefly explain your application reasons and related experience',
    'joinUs.agreeRules': 'I agree to abide by community rules and code of conduct',
    'joinUs.submitting': 'Submitting...',
    'joinUs.submitApplicationBtn': 'Submit Application',
    'joinUs.fillAllRequired': 'Please fill in all required fields',
    'joinUs.submitSuccess': 'Application submitted successfully, we will review and contact you soon',
    'joinUs.submitFailed': 'Application submission failed, please try again later',

    // Business Cooperation
    'businessCooperation.title': 'Business Cooperation',
    'businessCooperation.description': 'We welcome various business cooperation related to community development',
    'businessCooperation.cooperationTypes': 'Cooperation Types',
    'businessCooperation.contactInfo': 'Contact Information',
    'businessCooperation.strategicPartners': 'Strategic Partners',
    'businessCooperation.loading': 'Loading...',

    // Privacy Policy
    'privacyPolicy.title': 'Peach Blossom Forum Privacy Policy',
    'privacyPolicy.version': 'Privacy Policy v1.0',
    'privacyPolicy.lastUpdated': 'Last Updated',
    'privacyPolicy.loading': 'Loading...',
    'privacyPolicy.fraudAlert': 'Fraud Alert',
    'privacyPolicy.fraudAlertDesc': 'Dear users, please be aware of the following scams:',
    'privacyPolicy.scamType1': 'Impersonating website administrators or customer service, asking you to provide account passwords, verification codes, or pay fees',
    'privacyPolicy.scamType2': 'Claiming your account has security issues requiring "verification" or "upgrade"',
    'privacyPolicy.scamType3': 'Inducing you to click links or provide personal information through "prizes" or "promotions"',
    'privacyPolicy.scamType4': 'Sending suspicious links or files via Telegram, WeChat, etc.',
    'privacyPolicy.scamType5': 'Asking you to transfer money or recharge to specified accounts',
    'privacyPolicy.safetyTip': 'Safety Tips:',
    'privacyPolicy.safetyTip1': 'This website will never ask for your password or verification code via phone, SMS, or email',
    'privacyPolicy.safetyTip2': 'Do not click on unknown links or download suspicious files',
    'privacyPolicy.safetyTip3': 'Do not share your account information and password with any individual or organization',
    'privacyPolicy.safetyTip4': 'If you encounter suspicious situations, please verify through official channels',
    'privacyPolicy.safetyTip5': 'Regularly check your account activity and contact us immediately if you notice any anomalies',
    'privacyPolicy.section1': '1. Introduction',
    'privacyPolicy.intro1': 'Peach Blossom Forum (hereinafter referred to as "we", "this platform", or "Peach Blossom Forum") is committed to protecting user privacy and personal information security. This Privacy Policy aims to clearly explain how we collect, use, store, protect, and share your personal information, as well as the rights you have over this information.',
    'privacyPolicy.intro2': 'When you access our website, use our services, or interact with us, you indicate that you have read, understood, and agree to the terms of this Privacy Policy. If you do not agree with this policy, please do not use our services.',
    'privacyPolicy.intro3': 'This Privacy Policy takes effect from May 2026, and we reserve the right to update this policy at any time.',
    'privacyPolicy.section2': '2. Information We Collect',
    'privacyPolicy.section2Desc': 'The information we collect is divided into the following categories:',
    'privacyPolicy.section2_1': '2.1 Account Registration Information',
    'privacyPolicy.section2_1Desc': 'When you register for a Peach Blossom Forum account, we collect the following information:',
    'privacyPolicy.username': 'Username',
    'privacyPolicy.usernameDesc': 'Your chosen display name',
    'privacyPolicy.email': 'Email Address',
    'privacyPolicy.emailDesc': 'Used for account verification, password reset, and important notifications',
    'privacyPolicy.password': 'Password',
    'privacyPolicy.passwordDesc': 'Encrypted account access password',
    'privacyPolicy.avatar': 'Avatar (Optional)',
    'privacyPolicy.avatarDesc': 'Your uploaded profile picture',
    'privacyPolicy.bio': 'Bio (Optional)',
    'privacyPolicy.bioDesc': 'Your personal introduction',
    'privacyPolicy.telegram': 'Telegram Account (Optional)',
    'privacyPolicy.telegramDesc': 'Used for receiving notifications and contact',
    'privacyPolicy.section2_2': '2.2 Usage Behavior Information',
    'privacyPolicy.section2_2Desc': 'When you use our services, we automatically collect the following information:',
    'privacyPolicy.ip': 'IP Address',
    'privacyPolicy.ipDesc': 'Your device network address',
    'privacyPolicy.browser': 'Browser Information',
    'privacyPolicy.browserDesc': 'Browser type, version, and language settings',
    'privacyPolicy.device': 'Device Information',
    'privacyPolicy.deviceDesc': 'Device type, operating system version',
    'privacyPolicy.accessLog': 'Access Records',
    'privacyPolicy.accessLogDesc': 'Pages you visited, links you clicked, time spent',
    'privacyPolicy.interactionLog': 'Interaction Records',
    'privacyPolicy.interactionLogDesc': 'Posts, comments, likes, private messages, etc. that you published',
    'privacyPolicy.searchLog': 'Search Records',
    'privacyPolicy.searchLogDesc': 'Keywords you searched on the platform',
    'privacyPolicy.section2_3': '2.3 Transaction Information',
    'privacyPolicy.section2_3Desc': 'If you make paid transactions (such as purchasing membership, donations, etc.), we collect the following information:',
    'privacyPolicy.amount': 'Transaction Amount',
    'privacyPolicy.amountDesc': 'Amount you paid',
    'privacyPolicy.paymentMethod': 'Payment Method',
    'privacyPolicy.paymentMethodDesc': 'Payment channel you used (such as USDT TRC20/ERC20)',
    'privacyPolicy.transactionTime': 'Transaction Time',
    'privacyPolicy.transactionTimeDesc': 'Time of transaction',
    'privacyPolicy.transactionStatus': 'Transaction Status',
    'privacyPolicy.transactionStatusDesc': 'Whether the transaction was successful',
    'privacyPolicy.walletAddress': 'Wallet Address (Optional)',
    'privacyPolicy.walletAddressDesc': 'Cryptocurrency wallet address you used for payment',
    'privacyPolicy.section2_4': '2.4 Device Identifiers and Cookie Information',
    'privacyPolicy.section2_4Desc': 'We use cookies and similar technologies to enhance your experience:',
    'privacyPolicy.sessionCookie': 'Session Cookie',
    'privacyPolicy.sessionCookieDesc': 'Used to maintain your login status',
    'privacyPolicy.preferenceCookie': 'Preference Cookie',
    'privacyPolicy.preferenceCookieDesc': 'Remember your language, theme, and other preferences',
    'privacyPolicy.analyticsCookie': 'Analytics Cookie',
    'privacyPolicy.analyticsCookieDesc': 'Help us understand user behavior and optimize services',
    'privacyPolicy.deviceId': 'Device Identifier',
    'privacyPolicy.deviceIdDesc': 'Identify your device to provide consistent experience',
    'privacyPolicy.section2_5': '2.5 Third-Party Service Information',
    'privacyPolicy.section2_5Desc': 'We may collect the following information through third-party services:',
    'privacyPolicy.paymentProvider': 'Payment Service Provider',
    'privacyPolicy.paymentProviderDesc': 'Information necessary for processing transactions',
    'privacyPolicy.analyticsProvider': 'Analytics Service Provider',
    'privacyPolicy.analyticsProviderDesc': 'Website usage data statistics',
    'privacyPolicy.securityProvider': 'Security Service Provider',
    'privacyPolicy.securityProviderDesc': 'Prevent fraud and security threats',
    'privacyPolicy.section3': '3. How We Use Your Information',
    'privacyPolicy.section3Desc': 'The information we collect is primarily used for the following purposes:',
    'privacyPolicy.section3_1': '3.1 Providing and Maintaining Services',
    'privacyPolicy.section3_1Items': 'Create and manage your account; Provide access and personalized experience; Process transactions and payments; Maintain normal service operation',
    'privacyPolicy.section3_2': '3.2 Communication and Support',
    'privacyPolicy.section3_2Items': 'Send account verification emails; Respond to your inquiries and requests; Send important notifications (such as service changes, security alerts); Provide technical support',
    'privacyPolicy.section3_3': '3.3 Improvement and Optimization',
    'privacyPolicy.section3_3Items': 'Analyze user behavior to improve services; Optimize website performance and user experience; Develop new features and services; Conduct market research and analysis',
    'privacyPolicy.section3_4': '3.4 Security and Compliance',
    'privacyPolicy.section3_4Items': 'Prevent fraud and abuse; Detect and block security threats; Comply with legal and regulatory requirements; Protect platform and user security',
    'privacyPolicy.section3_5': '3.5 Personalized Experience',
    'privacyPolicy.section3_5Items': 'Recommend content based on your interests; Customize user interface and features; Provide personalized services and promotions',
    'privacyPolicy.section4': '4. Information Sharing',
    'privacyPolicy.section4Desc': 'We take your privacy seriously and strictly limit information sharing. Except in the following cases:',
    'privacyPolicy.section4_1': '4.1 Necessary Service Providers',
    'privacyPolicy.section4_1Desc': 'We may share necessary information with the following service providers:',
    'privacyPolicy.paymentProcessor': 'Payment Processor',
    'privacyPolicy.paymentProcessorDesc': 'Minimum information required for processing payments',
    'privacyPolicy.cloudProvider': 'Cloud Service Provider',
    'privacyPolicy.cloudProviderDesc': 'Host services and store data',
    'privacyPolicy.securityService': 'Security Service Provider',
    'privacyPolicy.securityServiceDesc': 'Protect platform security',
    'privacyPolicy.analyticsService': 'Analytics Service Provider',
    'privacyPolicy.analyticsServiceDesc': 'Optimize service experience',
    'privacyPolicy.section4_1Note': 'These service providers can only access information necessary to fulfill their duties and must comply with strict data protection requirements.',
    'privacyPolicy.section4_2': '4.2 Legal Requirements',
    'privacyPolicy.section4_2Desc': 'When we receive subpoenas, search warrants, or other legal requests, we may disclose your information as required by law.',
    'privacyPolicy.section4_3': '4.3 Protecting Rights and Security',
    'privacyPolicy.section4_3Desc': 'To protect the rights, property, or safety of Peach Blossom Forum, users, or the public, we may disclose necessary information.',
    'privacyPolicy.section4_4': '4.4 Business Changes',
    'privacyPolicy.section4_4Desc': 'In the event of a merger, acquisition, asset sale, or other business change, your information may be transferred to the new owner.',
    'privacyPolicy.section4_5': '4.5 User Consent',
    'privacyPolicy.section4_5Desc': 'We may share your information with your explicit consent.',
    'privacyPolicy.section5': '5. Information Storage and Security',
    'privacyPolicy.section5_1': '5.1 Data Storage',
    'privacyPolicy.section5_1Desc': 'Your personal information is stored on secure servers with the following security measures:',
    'privacyPolicy.encryptedStorage': 'Encrypted Storage',
    'privacyPolicy.encryptedStorageDesc': 'Sensitive information (such as passwords) is stored using encryption algorithms',
    'privacyPolicy.accessControl': 'Access Control',
    'privacyPolicy.accessControlDesc': 'Only authorized personnel can access your information',
    'privacyPolicy.regularBackup': 'Regular Backup',
    'privacyPolicy.regularBackupDesc': 'Data is backed up regularly to prevent loss',
    'privacyPolicy.securityAudit': 'Security Audit',
    'privacyPolicy.securityAuditDesc': 'Regular security audits and vulnerability testing',
    'privacyPolicy.section5_2': '5.2 Data Transmission Security',
    'privacyPolicy.section5_2Desc': 'We use HTTPS protocol to protect data transmission, ensuring your information is not intercepted or tampered with during transmission.',
    'privacyPolicy.section5_3': '5.3 Security Limitations',
    'privacyPolicy.section5_3Desc': 'Although we have taken strict security measures, no system is absolutely secure. We cannot guarantee that your information will be completely protected from unauthorized access. Please also take necessary measures to protect your account security, such as using strong passwords and enabling two-factor authentication.',
    'privacyPolicy.section6': '6. Your Rights',
    'privacyPolicy.section6Desc': 'According to applicable laws, you have the following rights:',
    'privacyPolicy.section6_1': '6.1 Right of Access',
    'privacyPolicy.section6_1Desc': 'You have the right to access the personal information we hold about you.',
    'privacyPolicy.section6_2': '6.2 Right to Rectification',
    'privacyPolicy.section6_2Desc': 'If your personal information is inaccurate or incomplete, you have the right to request correction.',
    'privacyPolicy.section6_3': '6.3 Right to Deletion',
    'privacyPolicy.section6_3Desc': 'In certain circumstances, you have the right to request deletion of your personal information.',
    'privacyPolicy.section6_4': '6.4 Right to Restriction of Processing',
    'privacyPolicy.section6_4Desc': 'You have the right to request restriction of processing of your personal information.',
    'privacyPolicy.section6_5': '6.5 Right to Data Portability',
    'privacyPolicy.section6_5Desc': 'You have the right to obtain a copy of your personal information for transfer to another service provider.',
    'privacyPolicy.section6_6': '6.6 Right to Withdraw Consent',
    'privacyPolicy.section6_6Desc': 'If you previously consented to our processing of your information, you have the right to withdraw that consent.',
    'privacyPolicy.section6_7': '6.7 How to Exercise Your Rights',
    'privacyPolicy.section6_7Desc': 'To exercise the above rights, please contact us through our contact information. We will respond to your request within a reasonable time.',
    'privacyPolicy.section7': '7. Data Retention',
    'privacyPolicy.section7Desc': 'We only retain your personal information for as long as necessary to fulfill the purposes described in this Privacy Policy:',
    'privacyPolicy.accountInfo': 'Account Information',
    'privacyPolicy.accountInfoDesc': 'Retained during account existence, and retained for a necessary period after account deletion to complete settlements and comply with requirements',
    'privacyPolicy.transactionInfo': 'Transaction Information',
    'privacyPolicy.transactionInfoDesc': 'Retained for at least five years to meet tax and audit requirements',
    'privacyPolicy.logData': 'Logs and Usage Data',
    'privacyPolicy.logDataDesc': 'Retained for no more than 90 days unless required longer by law',
    'privacyPolicy.userContent': 'User Content',
    'privacyPolicy.userContentDesc': 'Retained until you publish or delete it',
    'privacyPolicy.section7Note': 'When information is no longer needed, we will take reasonable measures to securely delete or anonymize this information.',
    'privacyPolicy.section8': '8. Third-Party Links and Services',
    'privacyPolicy.section8Desc': 'Our website may contain links to third-party websites or services. These third-party services have their own privacy policies, and we are not responsible for their content or privacy practices.',
    'privacyPolicy.section8Note': 'We recommend that you carefully read their privacy policies when visiting third-party websites.',
    'privacyPolicy.section9': '9. International Data Transfers',
    'privacyPolicy.section9Desc': 'Our servers are located in mainland China. If you access our services from other countries or regions, your information may be transferred to mainland China for processing.',
    'privacyPolicy.section9Note': 'We ensure that all international data transfers comply with applicable data protection regulations.',
    'privacyPolicy.section10': '10. Children\'s Privacy',
    'privacyPolicy.section10Desc': 'Our services are not directed to children under 13. We do not knowingly collect personal information from children. If you discover that we have inadvertently collected information from a child, please contact us and we will delete it immediately.',
    'privacyPolicy.section11': '11. Cookie Policy',
    'privacyPolicy.section11Desc': 'We use cookies and similar technologies to provide a better user experience. You can manage your cookie preferences through your browser settings.',
    'privacyPolicy.necessaryCookie': 'Essential Cookies',
    'privacyPolicy.necessaryCookieDesc': 'Required to maintain basic functions, cannot be disabled',
    'privacyPolicy.analyticsCookies': 'Analytics Cookies',
    'privacyPolicy.analyticsCookiesDesc': 'Help us understand user behavior',
    'privacyPolicy.preferenceCookies': 'Preference Cookies',
    'privacyPolicy.preferenceCookiesDesc': 'Remember your settings',
    'privacyPolicy.section12': '12. Privacy Policy Updates',
    'privacyPolicy.section12Desc': 'We may update this Privacy Policy from time to time. Significant changes will be notified to you through website announcements or email.',
    'privacyPolicy.section12Note': 'We encourage you to review this Privacy Policy regularly to stay informed of any updates.',
    'privacyPolicy.section13': '13. Contact Us',
    'privacyPolicy.section13Desc': 'If you have any questions or concerns about this Privacy Policy, please contact us through the following methods:',
    'privacyPolicy.contactEmail': 'Email',
    'privacyPolicy.contactTelegram': 'Telegram',
    'privacyPolicy.loadingText': 'Loading...',

    // Terms of Service
    'termsOfService.title': 'Peach Blossom Forum Terms of Service',
    'termsOfService.version': 'Terms of Service v1.0',
    'termsOfService.lastUpdated': 'Last Updated',
    'termsOfService.back': 'Back',
    'termsOfService.welcome': 'Welcome to use Peach Blossom Forum (hereinafter referred to as "this platform", "this forum", or "Peach Blossom Forum"). This agreement is a service agreement between you and Peach Blossom Forum. Please read all the contents of this agreement carefully. This agreement aims to clarify the rights and obligations of both parties, especially emphasizing core principles such as room owner responsibility independence, platform not participating in content, no verification of authenticity, and users bearing their own risks.',
    'termsOfService.section1': '1. Service Overview',
    'termsOfService.section1_1': '1.1 Peach Blossom Forum is an online communication platform that provides users with services including but not limited to:',
    'termsOfService.section1_1Items': 'Creating and managing private rooms for private communication; Purchasing room memberships for exclusive benefits; Posting and browsing posts and comments; Participating in community discussions and interactions; Using the internal messaging system for communication; Donation and tip functions; Cloud storage space services; Other value-added services',
    'termsOfService.section1_2': '1.2 Services provided by this platform may be adjusted and optimized based on business development. Peach Blossom Forum reserves the right to change, suspend, or terminate part or all of the services at any time, but will notify users in advance.',
    'termsOfService.section1_3': '1.3 Some features of this platform may require payment. The specific description and price of paid features are subject to the platform announcement.',
    'termsOfService.section1_4': '1.4 This platform provides services following the principles of equality, voluntariness, fairness, and good faith.',
    'termsOfService.section2': '2. User Registration and Account Management',
    'termsOfService.section2_1': '2.1 Users need to register an account before using this platform services. Registration requires providing true, accurate, and complete information, including but not limited to username, email address, and other necessary information.',
    'termsOfService.section2_2': '2.2 Users should keep their account and password properly and are responsible for all activities conducted using the account. Any losses caused by account leakage, theft, or password loss shall be borne by the user.',
    'termsOfService.section2_3': '2.3 Users shall not transfer, rent, lend, sell, or otherwise permit others to use their account. If abnormal account usage or security risks are found, Peach Blossom Forum has the right to suspend or terminate the account.',
    'termsOfService.section2_4': '2.4 Users should ensure that contact information provided (such as email, phone number) is accurate and valid to receive important notices and verification information. If contact information changes, it should be updated promptly.',
    'termsOfService.section2_5': '2.5 Users understand and agree that Peach Blossom Forum has the right to review user registration information and request users to provide identity documents or other materials to complete real-name verification.',
    'termsOfService.section2_6': '2.6 After an account is suspended or terminated, users will not be able to log into this platform using that account, and paid services may not be refundable.',
    'termsOfService.section3': '3. User Rights and Obligations',
    'termsOfService.section3_1': '3.1 Users have the right to use various services provided by this platform under the premise of complying with this agreement.',
    'termsOfService.section3_2': '3.2 Users have the right to edit, modify, or delete content they publish, but should bear corresponding responsibilities.',
    'termsOfService.section3_3': '3.3 Users should comply with national laws and regulations, social morality, and public order and good customs, and shall not use this platform for any illegal activities.',
    'termsOfService.section3_4': '3.4 Content published by users on this platform must comply with laws, regulations, and this agreement, and shall not include the following:',
    'termsOfService.section3_4Items': 'Content violating laws and regulations; Content endangering national security; Content damaging national honor; Content inciting ethnic hatred; Content promoting cults; Content spreading rumors; Content involving illegal activities; Content infringing on rights; Content with false information; Other violating content',
    'termsOfService.section3_5': '3.5 Users should respect the rights of other users and shall not engage in harassment, intimidation, fraud, or other malicious behavior.',
    'termsOfService.section3_6': '3.6 Users shall not crack, decompile, or attempt to bypass the security measures of this platform.',
    'termsOfService.section3_7': '3.7 Users shall not use this platform for money laundering or other illegal activities.',
    'termsOfService.section4': '4. Content Responsibility Statement (Core Terms)',
    'termsOfService.section4_1': '4.1 Room Owner Responsibility Independence Principle (Most Important)',
    'termsOfService.section4_1Desc': 'This platform only provides technical services and platform support, and does not participate in or intervene in any relationships or transactions between room owners and users. Room owners bear full legal responsibility for all content published in their rooms.',
    'termsOfService.section4_2': '4.2 Platform Non-Participation in Content Principle',
    'termsOfService.section4_2Desc': 'This platform does not participate in, intervene in, or edit any statements published by room owners or other users in rooms.',
    'termsOfService.section4_3': '4.3 No Verification of Authenticity Principle',
    'termsOfService.section4_3Desc': 'This platform does not make any judgment or guarantee regarding the authenticity of content published by users. Users should verify information independently.',
    'termsOfService.section4_4': '4.4 No Guarantee Principle',
    'termsOfService.section4_4Desc': 'This platform does not guarantee that content is accurate, complete, timely, or reliable. Users should independently evaluate content risk.',
    'termsOfService.section4_5': '4.5 Investment Advice Disclaimer (Particularly Important)',
    'termsOfService.section4_5Desc': 'This platform may contain investment advice. Users should understand that any investment carries risks. Investment decisions are made by users themselves, and profits and losses are entirely borne by users.',
    'termsOfService.section4_6': '4.6 Transaction Parties Responsibility Principle',
    'termsOfService.section4_6Desc': 'Transactions on this platform are conducted only between users and this platform. For any disputes, the parties should resolve them independently.',
    'termsOfService.section4_7': '4.7 Third-Party Content Disclaimer',
    'termsOfService.section4_7Desc': 'This platform may contain third-party content, advertisements, or links. This platform is not responsible for the authenticity, legality, or security of third-party content.',
    'termsOfService.section5': '5. Risk Taking',
    'termsOfService.section5_1': '5.1 Users Bear All Risks (Core Term)',
    'termsOfService.section5_1Desc': 'Users bear all risks, losses, disputes, and legal responsibilities arising from using this platform services.',
    'termsOfService.section5_2': '5.2 Information Security Risk',
    'termsOfService.section5_2Desc': 'Users should protect their personal information security. This platform is not responsible for losses caused by personal information leakage.',
    'termsOfService.section5_3': '5.3 Transaction Risk',
    'termsOfService.section5_3Desc': 'Transactions on this platform carry risks including payment failure, transaction delay, and fund loss.',
    'termsOfService.section5_4': '5.4 Service Interruption Risk',
    'termsOfService.section5_4Desc': 'This platform may experience service interruption due to technical failures, maintenance, attacks, etc. Users should understand and accept this risk.',
    'termsOfService.section5_5': '5.5 Content Risk',
    'termsOfService.section5_5Desc': 'Users may experience psychological, emotional, or financial discomfort or loss from certain content on this platform.',
    'termsOfService.section5_6': '5.6 Legal Risk',
    'termsOfService.section5_6Desc': 'Users should understand and comply with relevant laws and regulations regarding online speech and investment transactions.',
    'termsOfService.section6': '6. Service Fees and Payment',
    'termsOfService.section6_1': '6.1 Some features may require payment. Specific fees are subject to platform announcement.',
    'termsOfService.section6_2': '6.2 Users should pay according to platform regulations. Supported payment methods include USDT (TRC20, ERC20) and other cryptocurrencies.',
    'termsOfService.section6_3': '6.3 Users are responsible for their payment behavior and ensuring sufficient funds.',
    'termsOfService.section6_4': '6.4 This platform reserves the right to adjust service fees at any time.',
    'termsOfService.section6_5': '6.5 This platform is not responsible for losses caused by cryptocurrency price fluctuations.',
    'termsOfService.section6_6': '6.6 Users understand and agree that cryptocurrency transactions are irreversible.',
    'termsOfService.section7': '7. Platform Rights and Obligations',
    'termsOfService.section7_1': '7.1 This platform has the right to delete, block, hide non-compliant content or restrict, suspend, terminate non-compliant users based on legal requirements.',
    'termsOfService.section7_2': '7.2 This platform has the right to review, edit, modify, or delete user-published content, but does not guarantee completeness and timeliness of review.',
    'termsOfService.section7_3': '7.3 This platform has the obligation to maintain normal platform operation and take reasonable security measures to protect user data security.',
    'termsOfService.section7_4': '7.4 This platform has the obligation to provide necessary technical support and service instructions to users.',
    'termsOfService.section7_5': '7.5 This platform has the right to formulate and modify platform rules and management regulations.',
    'termsOfService.section7_6': '7.6 This platform has the right to perform maintenance, upgrades, or adjustments on the platform when necessary.',
    'termsOfService.section8': '8. Privacy Policy',
    'termsOfService.section8_1': '8.1 This platform respects user privacy and will collect, use, store, and protect user personal information in accordance with the Privacy Policy.',
    'termsOfService.section8_2': '8.2 Users should carefully read the Privacy Policy to understand the scope, usage methods, and protection measures of personal information.',
    'termsOfService.section8_3': '8.3 Users using this platform services agree that Peach Blossom Forum processes user personal information in accordance with the Privacy Policy.',
    'termsOfService.section9': '9. Intellectual Property',
    'termsOfService.section9_1': '9.1 The name, logo, design, interface, functions, etc. of this platform are owned by Peach Blossom Forum and shall not be used, copied, modified, or distributed without authorization.',
    'termsOfService.section9_2': '9.2 Users retain intellectual property rights for content published on this platform, but grant this platform a global, free, non-exclusive usage right for platform operation and display.',
    'termsOfService.section9_3': '9.3 If users find content infringing their intellectual property rights on this platform, they can submit an infringement notice.',
    'termsOfService.section10': '10. Disclaimer',
    'termsOfService.section10_1': '10.1 This platform is not responsible for service interruption, data loss, system failure, transaction failure, etc. caused by force majeure, third-party reasons, or other non-platform faults.',
    'termsOfService.section10_2': '10.2 This platform is not responsible for any indirect losses, consequential damages, or punitive damages arising from users using the service.',
    'termsOfService.section10_3': '10.3 This platform is not responsible for any disputes, disagreements, or losses between users arising from using the service.',
    'termsOfService.section10_4': '10.4 This platform is not responsible for any losses caused by network reasons, hacker attacks, system failures, or third-party service interruptions.',
    'termsOfService.section10_5': '10.5 The disclaimer in this agreement is valid to the maximum extent permitted by law.',
    'termsOfService.section11': '11. Agreement Modification and Termination',
    'termsOfService.section11_1': '11.1 This platform reserves the right to modify this agreement at any time. Modified agreements will take effect from the date of announcement.',
    'termsOfService.section11_2': '11.2 Continued use of services constitutes acceptance of the modified agreement.',
    'termsOfService.section11_3': '11.3 Users can stop using this platform services at any time, and services will terminate after account cancellation.',
    'termsOfService.section11_4': '11.4 This platform has the right to terminate part or all of the services without assuming breach of contract liability.',
    'termsOfService.section11_5': '11.5 After account termination, users remain responsible for content published before termination.',
    'termsOfService.section12': '12. Applicable Law and Dispute Resolution',
    'termsOfService.section12_1': '12.1 This agreement is governed by the laws of the People\'s Republic of China.',
    'termsOfService.section12_2': '12.2 Any dispute arising from or related to this agreement shall first be resolved through friendly negotiation; if negotiation fails, either party may file a lawsuit in the competent people\'s court.',
    'termsOfService.section13': '13. Other Terms',
    'termsOfService.section13_1': '13.1 The right of interpretation of this agreement belongs to Peach Blossom Forum.',
    'termsOfService.section13_2': '13.2 Matters not covered in this agreement shall be handled in accordance with national laws, industry practices, and other platform regulations.',
    'termsOfService.section13_3': '13.3 If this agreement conflicts with national laws and regulations, the laws and regulations shall prevail, but this does not affect the validity of other clauses.',
    'termsOfService.section13_4': '13.4 The headings of each clause in this agreement are for reading convenience only and do not affect the interpretation of the clause content.',
    'termsOfService.importantNotice': 'Important Notice (Please Read Carefully)',
    'termsOfService.importantNoticeDesc': 'Please carefully read all contents of this agreement before using this platform services. By clicking the "Agree" button or using this platform services, you indicate that you have read, understood, and agree to comply with all terms of this agreement.',
    'termsOfService.importantNoticeDesc2': 'This agreement aims to clarify the rights and obligations between the platform and users, especially emphasizing the following core principles:',
    'termsOfService.importantNoticeItems': 'Room owner responsibility independence: Statements made by room owners in rooms are personal behavior and have nothing to do with the platform; Platform does not participate in content: Platform is not responsible for user-published content; No verification of authenticity: Platform does not verify information authenticity; Investment risk is borne by users: Any investment decisions are made by users themselves, profits and losses are borne by users; Users bear all risks: Any losses arising from using platform services are borne by users',
    'termsOfService.importantNoticeDesc3': 'If you do not agree with any terms of this agreement, please do not use this platform services.',

    // Admin Additional
    'admin.loadDataFailed': 'Failed to load data',
    'admin.loadUsersFailed': 'Failed to load user list',
    'admin.loadUserGroupsFailed': 'Failed to load user group list',
    'admin.userGroupUpdated': 'User group updated successfully',
    'admin.userGroupUpdateFailed': 'Failed to update user group',
    'admin.userGroupCreated': 'User group created successfully',
    'admin.userGroupCreateFailed': 'Failed to create user group',
    'admin.userGroupDeleted': 'User group deleted successfully',
    'admin.userGroupDeleteFailed': 'Failed to delete user group',
    'admin.saveUserGroupFailed': 'Failed to save user group',
    'admin.userDeleted': 'User deleted successfully',
    'admin.userDeleteFailed': 'Failed to delete user',
    'admin.userSaved': 'User information saved',
    'admin.saveFailed': 'Save failed',
    'admin.loadCategoriesFailed': 'Failed to load category list',
    'admin.categoryUpdated': 'Category updated',
    'admin.categoryCreated': 'Category created',
    'admin.categoryDeleted': 'Category deleted',
    'admin.categoryDeleteFailed': 'Failed to delete category',
    'admin.categorySaveFailed': 'Failed to save category',
    'admin.categoryShow': 'Category shown',
    'admin.categoryHide': 'Category hidden',
    'admin.loadPostsFailed': 'Failed to load post list',
    'admin.postDeleted': 'Post deleted',
    'admin.postDeleteFailed': 'Failed to delete post',
    'admin.postMoved': 'Post moved',
    'admin.postMoveFailed': 'Move failed',
    'admin.essenceSuccess': 'Set as essence',
    'admin.essenceCancel': 'Essence cancelled',
    'admin.loadSensitiveWordsFailed': 'Failed to load sensitive word list',
    'admin.sensitiveWordAdded': 'Sensitive word added',
    'admin.sensitiveWordAddFailed': 'Failed to add sensitive word',
    'admin.sensitiveWordDeleted': 'Sensitive word deleted',
    'admin.sensitiveWordDeleteFailed': 'Failed to delete sensitive word',
    'admin.sensitiveWordsImported': 'Successfully imported {count} sensitive words',
    'admin.sensitiveWordsImportFailed': 'Batch import failed',
    'admin.loadConfigFailed': 'Failed to load config',
    'admin.configUpdated': 'Config updated',
    'admin.configUpdateFailed': 'Failed to update config',
    'admin.loadAdSpotsFailed': 'Failed to load ad spots',
    'admin.loadAdsFailed': 'Failed to load ad list',
    'admin.adSpotOrderUpdated': 'Ad spot order updated',
    'admin.adSpotOrderUpdateFailed': 'Failed to update ad spot order',
    'admin.adOrderUpdated': 'Ad order updated',
    'admin.adOrderUpdateFailed': 'Failed to update order, please retry',
    'admin.adUpdated': 'Ad updated',
    'admin.adCreated': 'Ad added',
    'admin.adSaveFailed': 'Failed to save ad',
    'admin.adDeleted': 'Ad deleted',
    'admin.adDeleteFailed': 'Failed to delete ad',
    'admin.totalUsers': 'Total Users',
    'admin.totalPosts': 'Total Posts',
    'admin.todayPosts': 'Today Posts',
    'admin.totalReplies': 'Total Replies',
    'admin.searchUsers': 'Search users...',
    'admin.search': 'Search',
    'admin.saveChanges': 'Save Changes',
    'admin.confirmDelete': 'Confirm Delete',
    'admin.confirmDeleteUser': 'Are you sure you want to delete this user? This action cannot be undone!',
    'admin.confirmDeleteUserGroup': 'Are you sure you want to delete this user group? This action cannot be undone!',
    'admin.confirmDeleteCategory': 'Are you sure you want to delete this category? This action cannot be undone!',
    'admin.confirmDeletePost': 'Are you sure you want to delete this post? This action cannot be undone!',
    'admin.confirmDeleteSensitiveWord': 'Are you sure you want to delete this sensitive word? This action cannot be undone!',
    'admin.confirmDeleteAd': 'Are you sure you want to delete this ad? This action cannot be undone!',
    'admin.confirmOperation': 'Confirm Operation',
    'admin.confirmBan': 'Are you sure you want to ban this user?',
    'admin.confirmUnban': 'Are you sure you want to unban this user?',
    'admin.confirm': 'Confirm',
    'admin.confirmMove': 'Confirm Move',
    'admin.users': 'Users',
    'admin.userGroups': 'User Groups',
    'admin.noUserGroup': 'No User Group',
    'admin.createTime': 'Created At',
    'admin.noUserGroups': 'No user groups yet',
    'admin.createFirstUserGroup': 'Create First User Group',
    'admin.createUserGroup': 'Create User Group',
    'admin.editUserGroup': 'Edit User Group',
    'admin.userGroupName': 'User Group Name',
    'admin.enterUserGroupName': 'Please enter user group name',
    'admin.userGroupDescription': 'User Group Description',
    'admin.enterUserGroupDescription': 'Please enter user group description',
    'admin.selectRoleTip': 'Select another user group as the corresponding role classification, or leave empty for no corresponding role',
    'admin.createChannel': 'Create Channel',
    'admin.auditPosts': 'Audit Posts',
    'admin.appointRole': 'Appoint User Role',
    'admin.category': 'Category',
    'admin.posts': 'Posts',
    'admin.noDescription': 'No description',
    'admin.show': 'Show',
    'admin.hide': 'Hide',
    'admin.addCategory': 'Add Category',
    'admin.editCategory': 'Edit Category',
    'admin.addPost': 'Add Post',
    'admin.searchPosts': 'Search posts...',
    'admin.normal': 'Normal',
    'admin.deleted': 'Deleted',
    'admin.searchSensitiveWords': 'Search sensitive words...',
    'admin.add': 'Add',
    'admin.noSensitiveWords': 'No sensitive words yet',
    'admin.configsTotal': 'Total Configs',
    'admin.loading': 'Loading...',
    'admin.today': 'There are new activities waiting for you to handle today',
    'admin.selectOrCreateRoom': 'Select a room to manage or create a new one',
    
    // Admin Master Specific
    'admin.welcomeBack': 'Welcome back, {name}!',
    'admin.adminControlPanel': 'This is your community management dashboard, there are new activities waiting for you to handle',
    'admin.dashboardStats': 'Data Trends',
    'admin.realTime': 'Real-time',
    'admin.minutes5': '5 minutes',
    'admin.hour1': '1 hour',
    'admin.hours24': '24 hours',
    'admin.days7': '7 days',
    'admin.autoRefresh': 'Auto-refresh every 5 seconds',
    'admin.autoRefreshMinutes': 'Auto-refresh every {minutes} minutes',
    'admin.autoRefreshHour': 'Auto-refresh every hour',
    'admin.autoRefreshDay': 'Auto-refresh daily',
    'admin.autoRefreshWeek': 'Auto-refresh every {days} days',
    'admin.userManagement': 'User Management',
    'admin.searchUserPlaceholder': 'Search users...',
    'admin.reset': 'Reset',
    'admin.refresh': 'Refresh',
    'admin.userId': 'User ID',
    'admin.email': 'Email',
    'admin.emailVerification': 'Email Verification',
    'admin.registerTime': 'Register Time',
    'admin.verified': 'Verified',
    'admin.notVerified': 'Not Verified',
    'admin.deleteUser': 'Delete User',
    'admin.disableUser': 'Disable User',
    'admin.enableUser': 'Enable User',
    'admin.editEmail': 'Edit Email',
    'admin.sendVerificationEmail': 'Send Verification Email',
    'admin.sendResetPasswordEmail': 'Send Reset Password Email',
    'admin.cancel': 'Cancel',
    'admin.confirmDelete': 'Confirm Delete',
    'admin.confirmDeleteUser': 'Are you sure you want to delete this user? This action cannot be undone!',
    'admin.confirmDeleteUserGroup': 'Are you sure you want to delete this user group? This action cannot be undone!',
    'admin.confirmDeleteCategory': 'Are you sure you want to delete this category? This action cannot be undone!',
    'admin.confirmDeletePost': 'Are you sure you want to delete this post? This action cannot be undone!',
    'admin.confirmDeleteSensitiveWord': 'Are you sure you want to delete this sensitive word? This action cannot be undone!',
    'admin.confirmDeleteAd': 'Are you sure you want to delete this ad? This action cannot be undone!',
    'admin.confirmOperation': 'Confirm Operation',
    'admin.confirmBan': 'Are you sure you want to ban this user?',
    'admin.confirmUnban': 'Are you sure you want to unban this user?',
    'admin.confirmSendVerification': 'Are you sure you want to send a verification email?',
    'admin.confirmSendResetPassword': 'Are you sure you want to send a reset password email?',
    'admin.userDisabled': 'User disabled',
    'admin.userEnabled': 'User enabled',
    'admin.verificationEmailSent': 'Verification email sent',
    'admin.resetPasswordEmailSent': 'Reset password email sent',
    'admin.userDeleted': 'User deleted',
    'admin.deleteUserFailed': 'Failed to delete user',
    'admin.userInfoSaved': 'User information saved',
    'admin.saveFailed': 'Failed to save',
    'admin.searchPosts': 'Search posts...',
    'admin.movePost': 'Move Post',
    'admin.movePostToCategory': 'Move post to another category',
    'admin.currentCategory': 'Current Category',
    'admin.targetCategory': 'Target Category',
    'admin.globalTop': 'Global Pin',
    'admin.categoryTop': 'Category Pin',
    'admin.cancelTop': 'Cancel Pin',
    'admin.globalTopSuccess': 'Global pinned successfully',
    'admin.categoryTopSuccess': 'Category pinned successfully',
    'admin.cancelTopSuccess': 'Pin cancelled successfully',
    'admin.setEssence': 'Set as Essence',
    'admin.cancelEssence': 'Cancel Essence',
    'admin.setEssenceSuccess': 'Set as essence successfully',
    'admin.cancelEssenceSuccess': 'Essence cancelled successfully',
    'admin.postDeleted': 'Post deleted',
    'admin.deletePostFailed': 'Failed to delete post',
    'admin.movePostSuccess': 'Post moved successfully',
    'admin.movePostFailed': 'Move failed',
    'admin.categoryManagement': 'Category Management',
    'admin.addCategory': 'Add Category',
    'admin.editCategory': 'Edit Category',
    'admin.categoryName': 'Category Name',
    'admin.enterCategoryName': 'Please enter category name',
    'admin.categoryDescription': 'Category Description',
    'admin.enterCategoryDescription': 'Please enter category description',
    'admin.topLevelCategory': 'Top Level Category',
    'admin.parentCategory': 'Parent Category',
    'admin.noParent': 'No Parent',
    'admin.sortOrder': 'Sort Order',
    'admin.enterSortOrder': 'Please enter sort order',
    'admin.createCategory': 'Create Category',
    'admin.updateCategory': 'Update Category',
    'admin.categoryCreated': 'Category created successfully',
    'admin.categoryUpdated': 'Category updated successfully',
    'admin.saveCategoryFailed': 'Failed to save category',
    'admin.categoryShown': 'Category shown',
    'admin.categoryHidden': 'Category hidden',
    'admin.categoryDeleted': 'Category deleted successfully',
    'admin.deleteCategoryFailed': 'Failed to delete category',
    'admin.sensitiveWordLibrary': 'Sensitive Word Library',
    'admin.addSensitiveWord': 'Add Sensitive Word',
    'admin.batchImport': 'Batch Import',
    'admin.word': 'Sensitive Word',
    'admin.enterWord': 'Please enter sensitive word',
    'admin.enterWordPlaceholder': 'Please enter sensitive word',
    'admin.batchImportPlaceholder': 'Batch import sensitive words, one per line',
    'admin.sensitiveWordAdded': 'Sensitive word added successfully',
    'admin.addSensitiveWordFailed': 'Failed to add sensitive word',
    'admin.batchImportFailed': 'Batch import failed',
    'admin.importedSuccess': 'Successfully imported {count} sensitive words',
    'admin.sensitiveWordDeleted': 'Sensitive word deleted successfully',
    'admin.deleteSensitiveWordFailed': 'Failed to delete sensitive word',
    'admin.siteConfig': 'Site Configuration',
    'admin.loadConfigFailed': 'Failed to load configuration',
    'admin.configUpdated': 'Configuration updated successfully',
    'admin.updateConfigFailed': 'Failed to update configuration',
    'admin.adManagement': 'Ad Management',
    'admin.loadAdSpotsFailed': 'Failed to load ad spots',
    'admin.adSpotOrderUpdated': 'Ad spot order updated successfully',
    'admin.updateAdSpotOrderFailed': 'Failed to update ad spot order',
    'admin.loadAdsFailed': 'Failed to load ad list',
    'admin.adNotFound': 'Ad not found',
    'admin.adOrderUpdated': 'Ad order updated successfully',
    'admin.updateAdOrderFailed': 'Failed to update order, please retry',
    'admin.adUpdated': 'Ad updated successfully',
    'admin.adCreated': 'Ad added successfully',
    'admin.saveAdFailed': 'Failed to save ad',
    'admin.adDeleted': 'Ad deleted successfully',
    'admin.deleteAdFailed': 'Failed to delete ad',
    'admin.adSpot': 'Ad Spot',
    'admin.adTitle': 'Title',
    'admin.adContent': 'Content',
    'admin.adImage': 'Image',
    'admin.adLink': 'Link',
    'admin.adType': 'Type',
    'admin.adImageType': 'Image',
    'admin.adCodeType': 'Code',
    'admin.adStartTime': 'Start Time',
    'admin.adEndTime': 'End Time',
    'admin.adActive': 'Active',
    'admin.adInactive': 'Inactive',
    'admin.adSortOrder': 'Sort Order',
    'admin.addAd': 'Add Ad',
    'admin.editAd': 'Edit Ad',
    'admin.deleteAd': 'Delete Ad',
    'admin.operationLogs': 'Operation Logs',
  },
};
